import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { Sparkle } from "../controls";
import { SectionCaptureTable, CapturedItemCard } from "./ReviewSave";
import ChiefComplaints from "../chiefComplaints/ChiefComplaints";
import ComplaintForm from "../chiefComplaints/ComplaintForm";
import { CC_V2_ADD, CC_V2_EDIT, CC_V2_EDIT_CAPTURED } from "../chiefComplaints/events";
import Diagnosis from "../diagnosis/Diagnosis";
import DiagnosisForm from "../diagnosis/DiagnosisForm";
import { DX_V2_ADD, DX_V2_STATUS, DX_V2_EDIT_CAPTURED } from "../diagnosis/events";
import Medicines from "../medicines/Medicines";
import MedicineForm from "../medicines/MedicineForm";
import { MED_V2_ADD, MED_V2_REFRESH, MED_V2_STATUS, MED_V2_EDIT_CAPTURED } from "../medicines/events";
import MedicineService from "../medicines/MedicineService";
import MedicineStatusForm from "../medicines/MedicineStatusForm";
import Hospitalisation from "../hospitalisation/Hospitalisation";
import HospitalisationForm from "../hospitalisation/HospitalisationForm";
import { HOSP_V2_ADD, HOSP_V2_EDIT, HOSP_V2_EDIT_CAPTURED } from "../hospitalisation/events";
import LabInvestigations from "../labInvestigations/LabInvestigations";
import LabInvestigationForm from "../labInvestigations/LabInvestigationForm";
import { LI_V2_EDIT } from "../labInvestigations/events";
import Allergies from "../allergies/Allergies";
import AllergyForm from "../allergies/AllergyForm";
import { ALG_V2_ADD, ALG_V2_EDIT, ALG_V2_EDIT_CAPTURED } from "../allergies/events";
import FamilyHistory from "../familyHistory/FamilyHistory";
import RelativeForm from "../familyHistory/RelativeForm";
import { FAM_V2_ADD, FAM_V2_EDIT, FAM_V2_EDIT_CAPTURED } from "../familyHistory/events";
import { isSingleRelationship } from "../familyHistory/data";
import Addictions from "../addictions/Addictions";
import AddictionForm from "../addictions/AddictionForm";
import { ADD_V2_EDIT } from "../addictions/events";
import PhysicalActivity from "../physicalActivity/PhysicalActivity";
import ActivityForm from "../physicalActivity/ActivityForm";
import { ACT_V2_ADD, ACT_V2_EDIT, ACT_V2_STATUS, ACT_V2_EDIT_CAPTURED } from "../physicalActivity/events";
import Anthropometric from "../anthropometric/Anthropometric";
import AnthropometricForm from "../anthropometric/AnthropometricForm";
import { ANTH_V2_SELECT_PARAM } from "../anthropometric/events";
import DailySchedule from "../dailySchedule/DailySchedule";
import ScheduleEntryForm from "../dailySchedule/ScheduleEntryForm";
import FoodPreference from "../foodPreference/FoodPreference";
import { FP_V2_AI_INJECT } from "../foodPreference/events";
import DrugSchedule from "../drugSchedule/DrugSchedule";
import DrugScheduleEditor from "../drugSchedule/DrugScheduleEditor";
import PatientDetails from "../patientDetails/PatientDetails";
import PatientDetailsEditor from "../patientDetails/PatientDetailsEditor";
import { bodyToCapturedUpdate as patientDetailsBodyToCapturedUpdate } from "./patientDetailsBodyAdapters";
import { DS_V2_EDIT_CAPTURED, DS_V2_SHEET_COUNT } from "../drugSchedule/events";
import { stagedAddToPlan as dsStagedAddToPlan } from "../drugSchedule/helpers";
import focusFirstFocusable from "../common/focusFirstFocusable";
import { useClearCardDraft } from "../common/useCardDraft";
import { bodyToCapturedAdd as drugScheduleBodyToCapturedAdd } from "./drugScheduleBodyAdapters";
import {
  bodyToCapturedAdd as ccBodyToCapturedAdd,
  bodyToCapturedUpdate as ccBodyToCapturedUpdate,
} from "./complaintBodyAdapters";
import {
  bodyToCapturedAdd as dxBodyToCapturedAdd,
  bodyToCapturedUpdate as dxBodyToCapturedUpdate,
} from "./diagnosisBodyAdapters";
import {
  bodyToCapturedAdd as medBodyToCapturedAdd,
} from "./medicineBodyAdapters";
import {
  bodyToCapturedAdd as hospBodyToCapturedAdd,
  bodyToCapturedUpdate as hospBodyToCapturedUpdate,
} from "./hospitalisationBodyAdapters";
import {
  bodyToCapturedAdd as labBodyToCapturedAdd,
  capturedItemsToCardEditing as labCapturedItemsToCardEditing,
  commitToPrimaryCapturedAdd as labCommitToPrimaryCapturedAdd,
} from "./labInvestigationBodyAdapters";
import DigitiseManuallyCard from "../labInvestigations/DigitiseManuallyCard";
import {
  bodyToCapturedAdd as algBodyToCapturedAdd,
  bodyToCapturedUpdate as algBodyToCapturedUpdate,
} from "./allergyBodyAdapters";
import {
  bodyToCapturedAdd as fhBodyToCapturedAdd,
  bodyToCapturedUpdate as fhBodyToCapturedUpdate,
} from "./familyHistoryBodyAdapters";
import {
  bodyToCapturedAdd as addBodyToCapturedAdd,
} from "./addictionBodyAdapters";
import {
  bodyToCapturedAdd as actBodyToCapturedAdd,
  bodyToCapturedUpdate as actBodyToCapturedUpdate,
} from "./activityBodyAdapters";
import {
  bodyToCapturedAdd as anthBodyToCapturedAdd,
} from "./anthropometricBodyAdapters";
import {
  bodyToCapturedAdd as dsBodyToCapturedAdd,
} from "./dailyScheduleBodyAdapters";
/**
 * S2 middle+right region of the AI-transcript modal.
 *
 * Layout matches spec board 2495:3060 — a horizontal tab strip at the
 * top, then a split below:
 *
 *   ┌─────────── section tabs ────────────────────────┐
 *   ├───────── table ─────────┬── cards column ───────┤
 *   │  Added items for the    │  Proposed items for   │
 *   │  active tab             │  the active tab       │
 *   │  (accepted by care mgr) │  (awaiting Add /      │
 *   │                         │   Discard)            │
 *   └─────────────────────────┴───────────────────────┘
 *
 * Items are split by state:
 *   proposed        → right column, as a review card with Add + Discard
 *   added_in_call   → middle table, as a row
 *   edited_in_call  → middle table, as a row
 *   discarded_*     → hidden (audit lives in the transcript doc)
 *
 * Per-tab badge counts count PROPOSED items only — the number that need
 * a decision. Added rows aren't attention-worthy after acceptance.
 *
 * Per-section rich forms (VAS status, Duration steppers, Consulted-
 * doctor toggle) are deliberately NOT rendered yet — this slice ships
 * a generic table + generic cards, matching the mock's shape without
 * the per-section columns. Section-specific rendering is Phase 2c.
 */

// Kebab-case tab id ↔ snake-case Go section string. Includes the full
// 12 v2 sections; missing ones map to null.
export const TAB_TO_SECTION = {
  "patient-details":   "patient_details",
  "anthropometric":    "anthropometric",
  "chief-complaints":  "chief_complaints",
  "diagnosis":         "diagnosis",
  "medicines":         "medicines",
  "hospitalisation":   "hospitalisation",
  "allergies":         "allergies",
  "family-history":    "family_history",
  "addictions":        "addictions",
  "lab-investigations": "lab_investigations",
  "food-preferences":  "food_preferences",
  "daily-schedule":    "daily_schedule",
  "activity":          "activity",
  "drug-schedule":     "drug_schedule",
};

// Reverse — Go section string → kebab-case tab id. Used by Phase 4's
// Edit-from-Review handler which has the Go slug but needs the tab id
// to drive setActiveSection (modal keeps tab-id state).
export const SECTION_TO_TAB = Object.fromEntries(
  Object.entries(TAB_TO_SECTION).map(([tab, section]) => [section, tab]),
);

// Human-facing section name for the table header ("Chief complaints · 3 entries").
const SECTION_HEADING = {
  patient_details:   "Patient details",
  chief_complaints:  "Chief complaints",
  diagnosis:         "Diagnosis",
  medicines:         "Medicines",
  hospitalisation:   "Hospitalisation history",
  allergies:         "Allergies",
  family_history:    "Family history",
  addictions:        "Addictions",
  anthropometric:    "Anthropometric data",
  lab_investigations: "Lab investigations",
  food_preferences:  "Food preferences",
  daily_schedule:    "Daily schedule",
  activity:          "Physical activity",
  drug_schedule:     "Drug schedule",
};

// Best-guess "title" field per section. Falls back to payload.value if
// the specific field is missing.
const TITLE_FIELD = {
  chief_complaints: "complaint",
  diagnosis:        "name",
  medicines:        "name",
  hospitalisation:  "name",
  allergies:        "name",
  family_history:   "condition",
  addictions:       "substance",
};

const titleFor = (item) => {
  const t = item && item.current && item.current[TITLE_FIELD[item.targetSection]];
  return (t && String(t)) || (item && item.current && item.current.value) || "(untitled)";
};

// Compact meta line — code + severity + duration if present. Same idea
// as the previous inline card meta.
const metaFor = (item) => {
  const p = (item && item.current) || {};
  const bits = [];
  if (p.icd_code)        bits.push(`ICD ${p.icd_code}`);
  else if (p.cpt_code)   bits.push(`CPT ${p.cpt_code}`);
  else if (p.loinc_code) bits.push(`LOINC ${p.loinc_code}`);
  if (p.since_when) bits.push(`since ${p.since_when}`);
  if (p.intensity)  bits.push(p.intensity);
  return bits.join(" · ");
};

// Payload fields already surfaced in title + meta; skip in the "other
// fields" list so we don't repeat info.
const HIDE_KEYS = new Set([
  "complaint", "name", "condition", "substance", "value",
  "icd_code", "cpt_code", "loinc_code",
  "since_when", "intensity",
  "icd_description", "cpt_description", "loinc_description",
  "confidence", "evidence",
  "icd_status", "icd_original_code", "icd_curated_code",
  "loinc_status", "loinc_llm_original_code", "loinc_llm_original_description",
  "cpt_status", "cpt_llm_original_code", "cpt_llm_original_description",
  "id", "field", "review_status", "timestamp", "edited",
]);

const otherFields = (payload) => {
  if (!payload) return [];
  return Object.keys(payload)
    .filter((k) => !HIDE_KEYS.has(k) && payload[k] != null && payload[k] !== "")
    .map((k) => ({ k, v: String(payload[k]) }));
};

// ─── Right-column proposal preview card ─────────────────────────────
//
// Rendered for items in state="proposed". Read-only during the call —
// the care manager reviews these visually while the conversation runs
// but takes NO action here. Accept/Discard/Edit all move to the review
// phase (post-call). The eyebrow shows the target section + code family.
const ProposalCard = ({ item }) => {
  const others = otherFields(item.current);
  const meta   = metaFor(item);
  return (
    <div className="aic-proposal">
      <div className="aic-proposal-head">
        <span className="aic-proposal-eyebrow">
          {SECTION_HEADING[item.targetSection] || item.targetSection}
          {item.current && item.current.icd_code ? " · ICD" : ""}
          {item.current && item.current.cpt_code ? " · CPT" : ""}
          {item.current && item.current.loinc_code ? " · LOINC" : ""}
        </span>
        <span className="aic-badge is-proposed">New</span>
      </div>
      <div className="aic-proposal-body">
        <p className="aic-proposal-title">{titleFor(item)}</p>
        {meta ? <p className="aic-proposal-meta">{meta}</p> : null}
        {others.length ? (
          <ul className="aic-proposal-kv">
            {others.map(({ k, v }) => (
              <li key={k}><span>{k}</span><em>{v}</em></li>
            ))}
          </ul>
        ) : null}
        {item.evidence ? (
          <p className="aic-proposal-evidence">“{item.evidence}”</p>
        ) : null}
      </div>
    </div>
  );
};
ProposalCard.propTypes = {
  item: PropTypes.object.isRequired,
};

// ─── Middle table row ───────────────────────────────────────────────
//
// Rendered for items in state="added_in_call" or "edited_in_call". Uses
// a generic 3-column layout (Title | Meta | Evidence) that reads
// reasonably across every section. Per-section columns (Chief
// complaints: Since when + Description + Status + Analysis) land in
// Phase 2c.
const TableRow = ({ item }) => (
  <div className="aic-trow" role="row">
    <span className="aic-tcell aic-trow-title" role="cell">{titleFor(item)}</span>
    <span className="aic-tcell aic-trow-meta"  role="cell">{metaFor(item) || "—"}</span>
    <span className="aic-tcell aic-trow-ev"    role="cell">
      {item.evidence ? `“${item.evidence}”` : "—"}
    </span>
  </div>
);
TableRow.propTypes = { item: PropTypes.object.isRequired };

// ─── Whole component ────────────────────────────────────────────────
//
// `patient` is threaded through so the Chief Complaints branch can hand
// it to the standalone `ChiefComplaints` component — the same component
// the sidebar section renders. The standalone owns its own CC v2 fetch;
// this file just embeds it.
// Standalone tab-strip renderer — extracted so AiTranscriptModal can
// render the tabs at the modal level (spanning the full grid width),
// leaving CapturedSections responsible for just the split content
// below. Pass `proposedCounts` when the caller has already computed
// per-section proposed counts; else omit and every tab renders without
// its numeric pill.
export const SectionTabs = ({ tabs, activeTab, onSelect, openCounts, modifiedSections, reviewedSections }) => (
  <div className="aic-tabs" role="tablist">
    {tabs.map((t) => {
      const section = TAB_TO_SECTION[t.id];
      const isReviewed = !!(section && reviewedSections && reviewedSections[section]);
      const n = section && openCounts ? openCounts[section] || 0 : 0;
      const isModified = !!(section && modifiedSections && modifiedSections.has(section));
      // ZY-10356 Phase 5 — reviewed sections carry a green ✓ prefix and
      // suppress both the orange dot and the open-count badge (design
      // doc §4.4 "a reviewed section cannot have open cards"; the
      // server-side guard enforces this but the frontend hides any
      // stale indicator too).
      const showDot = !isReviewed && (isModified || n > 0);
      const showCount = !isReviewed && n > 0;
      const extraCls = [
        t.id === activeTab ? "is-active" : "",
        n > 0 ? "has-open" : "",
        isModified ? "has-captures" : "",
        isReviewed ? "is-reviewed" : "",
      ].filter(Boolean).join(" ");
      return (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={t.id === activeTab}
          className={`aic-tab${extraCls ? ` ${extraCls}` : ""}`}
          onClick={() => onSelect(t.id)}
        >
          {isReviewed ? (
            <span className="aic-tab-check" aria-label="Section reviewed">&#10003;</span>
          ) : null}
          {showDot ? <span className="aic-tab-dot is-modified" aria-hidden="true" /> : null}
          {t.label}
          {showCount ? <span className="aic-tab-count">{n}</span> : null}
        </button>
      );
    })}
  </div>
);
SectionTabs.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({
    id:    PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  activeTab:  PropTypes.string.isRequired,
  onSelect:   PropTypes.func.isRequired,
  openCounts: PropTypes.object,
  /** Set of targetSection keys ("chief_complaints", "medicines", …)
   *  that carry at least one non-discarded captured item for the
   *  current transcript. Renders the orange "modified" dot on those
   *  section tabs. */
  modifiedSections: PropTypes.instanceOf(Set),
  /** Map of section → { reviewedAt, reviewedBy } from the transcript's
   *  reviewedSections registry. Reviewed sections get a green ✓ prefix
   *  on the tab and lose the count badge + modified dot (ZY-10356 §4.4). */
  reviewedSections: PropTypes.object,
};
SectionTabs.defaultProps = { openCounts: null, modifiedSections: null, reviewedSections: null };

// Compute per-section counts of items still in "proposed" state — used
// as the AI half of the tab counter. The full "open cards" number
// (proposed AI items + manually-opened editors currently on screen)
// lives inside CapturedSections since it also owns the manual /
// update / re-edit editor arrays; that value bubbles up via
// onOpenCountsChange.
export const proposedCountsByCapturedItems = (items) =>
  (items || []).reduce((acc, it) => {
    if (!it || it.state !== "pending") return acc;
    if (!it.targetSection) return acc;
    acc[it.targetSection] = (acc[it.targetSection] || 0) + 1;
    return acc;
  }, {});

const CapturedSections = ({
  tabs,
  activeTab,
  onSelectTab,
  items,
  patient,
  awaitingConsent,
  callEnded,
  patientId,
  appointmentId,
  transcriptId,
  transcriptService,
  version,
  // Ref owned by the modal, bumped SYNCHRONOUSLY on every poll /
  // mutation response. Preferred over the `version` prop (which
  // lags by one render whenever setSession hasn't flushed yet) so
  // the first mutation after a background bump doesn't 409.
  versionRef,
  onVersionSync,
  onItemsSync,
  // When true, the internal tab strip isn't rendered — caller draws
  // it externally (typically at the modal level so tabs can span
  // above the rail). Default false preserves the standalone shape.
  hideTabs,
  // "" = default (live-call / review flow — middle column renders
  // the standalone section component with captured items overlaid).
  // "review" = past-transcript view — middle column renders only
  // the items THIS transcript captured, using the shared S5 Review
  // tables. Right column collapses (no manual add / AI proposal
  // affordance when browsing a reopened transcript).
  viewMode,
  // Called with `{[section]: openCardCount}` whenever the set of
  // open cards changes. Modal uses it to (a) feed the SectionTabs
  // "● Label N" pill and (b) gate the Review call button on all
  // counts being zero after call-end.
  onOpenCountsChange,
  // ZY-10356 Phase 1 — modal-root ref: Map<transcriptId, bag>.
  // Hydrated once at mount; written back on unmount. Lets per-section
  // open-card arrays round-trip across transcript switches (unmount
  // cycle imposed by the keyed CapturedSections at the modal level).
  // Null in tests / standalone — bag defaults apply.
  openCardsStoreRef,
  // ZY-10356 Phase 6 — modal-level setter for reviewedSections, called
  // from applyMutation so commit/revert/reject responses refresh the
  // lockout state without waiting for the 2s poll.
  onReviewedSectionsSync,
  // ZY-10356 Phase 5 — map of section → { reviewedAt, reviewedBy } from
  // the transcript's reviewedSections registry. Drives:
  //   - green ✓ prefix on the matching SectionTabs tab
  //   - lockout banner above the active section's content
  //   - pointer-events overlay disabling interactions in the content
  // Server-side guard already rejects mutations on reviewed sections
  // (Phase 3.A ErrSectionReviewed); this prop only drives visuals.
  reviewedSections,
}) => {
  const activeSection = TAB_TO_SECTION[activeTab] || null;

  // Snapshot of this transcript's previously-saved open-card bag.
  // Read once per mount from the modal-root ref; every useState
  // initializer below hydrates from the corresponding bag field.
  const initialBag = (openCardsStoreRef && openCardsStoreRef.current)
    ? (openCardsStoreRef.current.get(transcriptId) || {})
    : {};

  // Local state for manual "+ Add complaints" blank cards. Each entry is
  // a plain string id — no data yet. On Save the card resolves + drops.
  const [manualCardIds, setManualCardIds] = useState(initialBag.manualCardIds || []);

  // ═════════════════════════════════════════════════════════════════
  //  LOCKED — Transcript-mode "no second blank card" rule (2026-09-21)
  // ═════════════════════════════════════════════════════════════════
  //  Standalone rails route "+ Add" through useSheetStack, which
  //  refuses to push a second blank sheet on top of an existing blank
  //  one AND pulses the blank sheet so the user can spot it in a tall
  //  stack. Transcript mode manages manual cards via per-section
  //  arrays here, so the same rule (with the same visual) has to live
  //  at this layer. Every manual form reports its filled state
  //  through `onCardFilledChange(cardId, bool)`; openManual handlers
  //  call `refuseAsBlank(cardId)` when a click is refused so the
  //  target sheet pulses (and scrolls into view). The pulse span is
  //  remounted on each refusal via a counter so the CSS animation
  //  genuinely replays.
  //
  //  Locked components (do NOT weaken silently — confirm with the
  //  owner before touching):
  //    (a) cardFilledRef + onCardFilledChange — the shared filled-map.
  //    (b) findBlankCardId / hasBlankManualCard — the guard predicate.
  //    (c) refuseAsBlank + the `nudge` state — the refusal pulse.
  //    (d) Every openManual handler across the 8 covered sections
  //        (CC, Dx, Med, Hosp, Lab, Alg, FH, Activity) using the
  //        `const blankId = findBlankCardId(prev); if (blankId) {
  //        refuseAsBlank(blankId); return prev; }` pattern.
  //    (e) DigitiseManuallyCard's onFilledChange effect (lab's cards
  //        would otherwise be permanently invisible to the guard).
  //    (f) Every manual-card render site is wrapped in a `.zy-sheet`
  //        div with `ref={registerCardNode}` + a conditional pulse
  //        `<span key={nudge.n} className="zy-sheet-pulse" ... />` —
  //        removing either kills the pulse animation.
  //
  //  See memory file `feedback_transcript_blank_guard_locked.md` for
  //  the full decision record. If a change genuinely needs to remove
  //  or weaken this behavior, surface it BEFORE editing — do not
  //  quietly delete the guard or the pulse wrapper.
  const cardFilledRef = useRef({});
  const onCardFilledChange = useCallback((cardId, filled) => {
    if (filled) {
      cardFilledRef.current[cardId] = true;
    } else {
      delete cardFilledRef.current[cardId];
    }
  }, []);
  const [nudge, setNudge] = useState({ id: null, n: 0 });
  const nudgeTimer = useRef(null);
  // cardId -> DOM node (so a refused click can scroll the pulsed card
  // into view). Written on each manual card's wrapper ref callback.
  const cardNodeRef = useRef({});
  // id of the card whose DOM node should grab focus on next mount.
  // Every openManual / openUpdate / openReedit handler wraps the fresh
  // cardId in `withPendingFocus(...)` so the reviewer's keystroke
  // lands in the new card (matches the standalone useSheetStack
  // pattern). Ref, not state: focus timing must be synchronous with
  // the ref callback.
  const pendingFocusRef = useRef(null);
  // Draft hook — forms persist in-progress field values keyed by cardId
  // through CardDraftProvider. On X-close / Reject we must clear the
  // draft, otherwise the next reopen (same cardId `r-${captured.id}`)
  // hydrates the ABANDONED edits instead of the committed snapshot.
  // Reported after Rule-1 pass (CC manual add → reopen → modify → X →
  // reopen showed modified values, expected last-saved).
  const clearCardDraft = useClearCardDraft();
  const withPendingFocus = useCallback((cardId) => {
    pendingFocusRef.current = cardId;
    return cardId;
  }, []);
  const registerCardNode = useCallback((cardId, node) => {
    if (node) {
      cardNodeRef.current[cardId] = node;
      if (pendingFocusRef.current === cardId) {
        pendingFocusRef.current = null;
        focusFirstFocusable(node);
      }
    } else {
      delete cardNodeRef.current[cardId];
    }
  }, []);
  const refuseAsBlank = useCallback((cardId) => {
    if (!cardId) return;
    setNudge((p) => ({ id: cardId, n: p.n + 1 }));
    const node = cardNodeRef.current[cardId];
    if (node && node.scrollIntoView) node.scrollIntoView({ block: "nearest" });
    if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
    nudgeTimer.current = setTimeout(() => {
      setNudge((p) => ({ id: null, n: p.n }));
    }, 1200);
  }, []);
  useEffect(() => () => {
    if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
  }, []);
  const findBlankCardId = useCallback(
    (ids) => (ids || []).find((id) => !cardFilledRef.current[id]) || null,
    [],
  );
  const hasBlankManualCard = useCallback((ids) => {
    return !!findBlankCardId(ids);
  }, [findBlankCardId]);

  // Update cards — care manager clicked a status chip on an existing
  // (untouched) CC v2 complaint. Each entry is {cardId, complaint} so
  // we can render an update-mode form pre-filled with that record.
  const [updateCards, setUpdateCards] = useState(initialBag.updateCards || []);
  // Re-edit cards — care manager clicked a status chip on a row that
  // already has a staged captured item (peach bg). Each entry is
  // {cardId, captured, ccv2Record} — the same underlying captured item
  // gets its payload updated via decideCaptured on Save.
  const [reeditCards, setReeditCards] = useState(initialBag.reeditCards || []);

  // Diagnosis parallel state — same shape, per-section arrays so cards
  // don't cross-render when the reviewer switches tabs. Later refactor
  // once sections 3+ come online: promote to a single generic array
  // keyed by section.
  const [manualCardIdsDx, setManualCardIdsDx] = useState(initialBag.manualCardIdsDx || []);
  const [updateCardsDx, setUpdateCardsDx] = useState(initialBag.updateCardsDx || []);
  const [reeditCardsDx, setReeditCardsDx] = useState(initialBag.reeditCardsDx || []);

  // Medicines — Wave A first-cut only has ADD-side state. Update-side
  // (MedicineStatusForm) integration lands in Wave A pass 2 alongside
  // agent update auto-routing on name/mCode match.
  const [manualCardIdsMed, setManualCardIdsMed] = useState(initialBag.manualCardIdsMed || []);
  const [reeditCardsMed, setReeditCardsMed] = useState(initialBag.reeditCardsMed || []);
  // Update cards for existing Medicine v2 rows the reviewer clicked in
  // the middle-column table (medicine name / STATUS chip). Mirrors CC's
  // updateCards shape: {cardId, medicine, mode}. mode="status" opens
  // MedicineStatusForm; other modes are Wave-2.
  const [updateCardsMed, setUpdateCardsMed] = useState(initialBag.updateCardsMed || []);

  // Patient details — single-record UPDATE section. Rather than
  // relying on PD_V2_EDIT through the event bus (which stops the
  // right-column form from opening reliably when the standalone
  // shell's own PatientDetailsEditor also listens), we own the
  // Editor's open + focus state here and pass them down as
  // controlled props. Only ONE editing session ever exists at a
  // time — matches the single-record semantics.
  const [patientDetailsOpen, setPatientDetailsOpen] = useState(!!initialBag.patientDetailsOpen);
  const [patientDetailsFocus, setPatientDetailsFocus] = useState(initialBag.patientDetailsFocus || null);
  /* The row's click token — see PatientDetails.onRowClick. Held here because
     this mode owns the rail's state, and without it a second click on the same
     row focuses nothing. */
  const [patientDetailsFocusAt, setPatientDetailsFocusAt] = useState(initialBag.patientDetailsFocusAt || 0);
  const openPatientDetailsRail = ({ focusField, focusAt } = {}) => {
    setPatientDetailsFocus(focusField || null);
    setPatientDetailsFocusAt(focusAt || 0);
    setPatientDetailsOpen(true);
  };
  const closePatientDetailsRail = () => {
    setPatientDetailsOpen(false);
    setPatientDetailsFocus(null);
  };

  // Hospitalisation — same first-cut shape as Meds. Only manual add + AI
  // proposal + re-edit-of-staged-add. Update card materialises when the
  // reviewer clicks an existing record from the middle-column table.
  const [manualCardIdsHosp, setManualCardIdsHosp] = useState(initialBag.manualCardIdsHosp || []);
  const [reeditCardsHosp, setReeditCardsHosp] = useState(initialBag.reeditCardsHosp || []);
  const [updateCardsHosp, setUpdateCardsHosp] = useState(initialBag.updateCardsHosp || []);

  // Lab investigations — same first-cut shape.
  const [manualCardIdsLab, setManualCardIdsLab] = useState(initialBag.manualCardIdsLab || []);
  const [reeditCardsLab, setReeditCardsLab] = useState(initialBag.reeditCardsLab || []);

  // Allergies — Wave B first-cut. Manual add + AI proposal + re-edit of
  // staged add. Update card materialises when the reviewer clicks an
  // existing allergy row in the middle-column table.
  const [manualCardIdsAlg, setManualCardIdsAlg] = useState(initialBag.manualCardIdsAlg || []);
  const [reeditCardsAlg, setReeditCardsAlg] = useState(initialBag.reeditCardsAlg || []);
  const [updateCardsAlg, setUpdateCardsAlg] = useState(initialBag.updateCardsAlg || []);

  // Family history — same first-cut shape as Allergies.
  const [manualCardIdsFh, setManualCardIdsFh] = useState(initialBag.manualCardIdsFh || []);
  const [reeditCardsFh, setReeditCardsFh] = useState(initialBag.reeditCardsFh || []);
  const [updateCardsFh, setUpdateCardsFh] = useState(initialBag.updateCardsFh || []);

  // Addictions — fixed 4-slot resource, no "add" concept. The user
  // opens an update card by clicking a row's name/chip in the embedded
  // Addictions component, which emits ADD_V2_EDIT with the persisted
  // row as `editing`. AI proposals + review re-edits reuse the same
  // form. `updateCardsAdd` carries {cardId, addiction} pairs.
  const [updateCardsAdd, setUpdateCardsAdd] = useState(initialBag.updateCardsAdd || []);
  const [reeditCardsAdd, setReeditCardsAdd] = useState(initialBag.reeditCardsAdd || []);

  // Physical activity — same first-cut shape as Allergies / FH.
  const [manualCardIdsAct, setManualCardIdsAct] = useState(initialBag.manualCardIdsAct || []);
  const [reeditCardsAct, setReeditCardsAct] = useState(initialBag.reeditCardsAct || []);
  const [updateCardsAct, setUpdateCardsAct] = useState(initialBag.updateCardsAct || []);
  // Base v2 lists per section, bubbled up by the standalone components
  // via their onRowsSync callback. Used to detect exact-name matches
  // between AI proposals and existing patient rows so a proposal for
  // an entry the patient already has renders as an update card on
  // that row instead of a duplicate add card.
  const [actRows, setActRows] = useState([]);
  const [algRows, setAlgRows] = useState([]);
  const [fhRows, setFhRows] = useState([]);
  // Addiction DB rows (the 4-slot persisted list from Addiction v2) —
  // needed for the AI-proposal card so an AI emission that only states
  // Status/Started still fills Frequency / Description / Totals from
  // the slot's persisted values rather than rendering blank. Addictions
  // never grows the row count; `onRowsSync` returns the 4-row array.
  const [addRows, setAddRows] = useState([]);
  // Anthropometric parameters (the fixed-7 list from Anthropometric v2
  // — height / weight / waist / hip / bloodGroup, with BMI + WHR
  // server-derived). Each entry carries a `current` with the persisted
  // value for that parameter. Used as a fallback seed for the right-
  // rail AI-proposal card so a thin AI emission (e.g. just a new
  // weight value) still displays the prior measurement's unit / date
  // under the AI delta.
  const [anthRows, setAnthRows] = useState([]);
  const [ccRows, setCcRows] = useState([]);
  const [dxRows, setDxRows] = useState([]);
  const [hospRows, setHospRows] = useState([]);
  const [medRows, setMedRows] = useState([]);

  // Anthropometric — grouped-parameter resource. No manual add card
  // affordance in the transcript modal (the standalone rail's accordion
  // handles that). Cards on the right come from AI proposals + review
  // re-edit. Reviewers who want to add a fresh measurement manually
  // still do it through the embedded Anthropometric component's
  // param-name click (ANTH_V2_SELECT_PARAM), which we listen for and
  // materialise as an update card.
  const [manualCardsAnth, setManualCardsAnth] = useState(initialBag.manualCardsAnth || []);
  const [reeditCardsAnth, setReeditCardsAnth] = useState(initialBag.reeditCardsAnth || []);

  // Daily schedule — timeline entries. Wave C first-cut supports only
  // add-entry captures; hydration + sleep captures are not wired yet.
  // No `+ Add timeline entry` shortcut here — the embedded DailySchedule
  // component keeps its own affordance (EntryModal opens over the
  // transcript modal via the shared portal), which handles manual entry
  // without competing with this rail. If we want a rail-side manual
  // card later, drop a listener for the DS event and mirror the
  // pattern from Activity.
  const [reeditCardsDs, setReeditCardsDs] = useState(initialBag.reeditCardsDs || []);

  // Live sheet count for the drug-schedule tab — DrugScheduleEditor
  // emits DS_V2_SHEET_COUNT with {count} whenever its stack length
  // changes. Drives BOTH the tab's "N open" pill (via `bump` below)
  // and the rail visibility gate (`dsSheetOpen = count > 0`), so we
  // no longer have to reconcile a boolean derived from separate
  // "open" and "close" events.
  const [dsSheetCount, setDsSheetCount] = useState(0);
  useEffect(() => {
    const onCount = (payload) => setDsSheetCount((payload && payload.count) || 0);
    EventService.addListener(DS_V2_SHEET_COUNT, onCount);
    return () => EventService.removeListener(DS_V2_SHEET_COUNT, onCount);
  }, []);
  const dsSheetOpen = dsSheetCount > 0;

  // ZY-10356 Phase 1 — track which heavy "whole-component holds state"
  // sections have ever been visited this mount so we can keep them
  // mounted (CSS-hidden) across tab switches. Drug schedule / Daily
  // schedule / Food preferences each hold their own form state inside
  // the component — unmounting on tab switch would lose everything the
  // reviewer typed. Lightweight-form sections keep using the ternary
  // because their field state round-trips via the CardDraftProvider
  // instead.
  const everVisitedRef = useRef(new Set());
  if (activeSection) everVisitedRef.current.add(activeSection);
  const hasVisited = (s) => everVisitedRef.current.has(s);

  // ZY-10356 Phase 1 — collect every preserve-across-transcript-switch
  // state atom into a single ref updated on every render, then save
  // the latest snapshot to the modal-root store on unmount. Pairs with
  // the initialBag hydration above. dsSheetCount is deliberately
  // excluded — it's re-emitted on mount by the DrugScheduleEditor.
  const latestBagRef = useRef({});
  latestBagRef.current = {
    manualCardIds, updateCards, reeditCards,
    manualCardIdsDx, updateCardsDx, reeditCardsDx,
    manualCardIdsMed, reeditCardsMed, updateCardsMed,
    patientDetailsOpen, patientDetailsFocus, patientDetailsFocusAt,
    manualCardIdsHosp, reeditCardsHosp, updateCardsHosp,
    manualCardIdsLab, reeditCardsLab,
    manualCardIdsAlg, reeditCardsAlg, updateCardsAlg,
    manualCardIdsFh, reeditCardsFh, updateCardsFh,
    updateCardsAdd, reeditCardsAdd,
    manualCardIdsAct, reeditCardsAct, updateCardsAct,
    manualCardsAnth, reeditCardsAnth,
    reeditCardsDs,
  };
  useEffect(() => {
    const store = openCardsStoreRef;
    const tid = transcriptId;
    return () => {
      if (!store || !store.current || !tid) return;
      store.current.set(tid, latestBagRef.current);
    };
    // Cleanup fires on unmount; deliberately not re-run on prop change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Payload-change watcher. When the agent merges a follow-up AI mention
  // into an already-decided capture (state=added_in_call/edited_in_call),
  // its payload changes on the server but its state stays put — so the
  // proposals filter no longer surfaces it as a card, but the reviewer
  // has never seen the new fields. This effect notices those
  // in-place payload updates and materialises a reedit card so the
  // reviewer can (a) inspect the merged fields, (b) Save & next to
  // persist them, or (c) Cancel to drop them. Cancel just closes the
  // card; the peach row's overlay tracks `payload` today so the "old
  // values until save" contract isn't fully honoured until we also
  // snapshot a committed payload — separate follow-up.
  // ZY-10387 Shifts 1/2/4 collapsed the frontend's reopen bookkeeping:
  //   - Shift 1 strips nulls from captured.current at the server, so no
  //     client-side null-filter is needed on the merge.
  //   - Shift 2 exposes captured.effective = {...lastCommitted, ...current}
  //     precomputed, so there's no merge to re-derive here either.
  //   - Shift 4 flips captured.reopenPending=true at the server only when
  //     an AI (actor=ai) re-emission lands on an already-committed item,
  //     so the previous sig-diff / seen-stamp machinery (prevPayloadRef,
  //     seenReopenRef, stableSig, markSaveCommitted) is no longer needed.
  //     A frontend-triggered merge doesn't flag itself, so the self-
  //     trigger guard that markSaveCommitted used to install is also
  //     unnecessary — it's now a server-side invariant.
  // Kept as a no-op stub so the 14 existing save-handler call sites
  // don't need to be touched in this batch; delete the stub + the calls
  // in a later cleanup pass.
  const markSaveCommitted = useRef(() => {}).current;
  useEffect(() => {
    for (const it of items || []) {
      if (!it || !it.id) continue;
      if (!it.reopenPending) continue;
      // Hand the reedit card the server-computed effective payload.
      // Fall back to current only for a backend that pre-dates Shift 2
      // (should never happen in prod but keeps dev-with-old-server from
      // throwing).
      const mergedInitialPayload = it.effective || it.current || {};
      switch (it.targetSection) {
        case "activity": {
          const record = it.operation === "update" && it.dbRecordRef
            ? (actRows || []).find((r) => String(r.activityId) === String(it.dbRecordRef))
            : null;
          setReeditCardsAct((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-act-${it.id}-${Date.now()}`, captured: it, actv2Record: record, initialPayloadOverride: mergedInitialPayload }, ...prev2]
          ));
          break;
        }
        case "allergies": {
          const record = it.operation === "update" && it.dbRecordRef
            ? (algRows || []).find((r) => String(r.allergyId) === String(it.dbRecordRef))
            : null;
          setReeditCardsAlg((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-alg-${it.id}-${Date.now()}`, captured: it, algv2Record: record, initialPayloadOverride: mergedInitialPayload }, ...prev2]
          ));
          break;
        }
        case "family_history": {
          const record = it.operation === "update" && it.dbRecordRef
            ? (fhRows || []).find((r) => String(r.relativeId) === String(it.dbRecordRef))
            : null;
          setReeditCardsFh((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-fh-${it.id}-${Date.now()}`, captured: it, famv2Record: record, initialPayloadOverride: mergedInitialPayload }, ...prev2]
          ));
          break;
        }
        case "hospitalisation": {
          // Resolve the backing Hosp v2 row by dbRecordRef so the
          // reedit renders through the hospv2Record update branch
          // (locked name/ICD) when the AI merge is an operation=update.
          // Null would fall through to the staged-add path and lose
          // the lock. Mirrors the activity / allergies / FH / CC / Dx
          // lookup.
          const record = it.operation === "update" && it.dbRecordRef
            ? (hospRows || []).find((r) => String(r.hospitalisationId) === String(it.dbRecordRef))
            : null;
          setReeditCardsHosp((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-hosp-${it.id}-${Date.now()}`, captured: it, hospv2Record: record, initialPayloadOverride: mergedInitialPayload }, ...prev2]
          ));
          break;
        }
        case "medicines": {
          // Resolve the backing Med v2 row by dbRecordRef so an AI
          // merge onto an operation=update captured renders through
          // the update branch instead of falling to staged-add. Match
          // is on medicineId (Med v2's primary row id). Null is fine
          // when the captured is operation=add (no dbRecordRef).
          const record = it.operation === "update" && it.dbRecordRef
            ? (medRows || []).find((r) => String(r.medicineId) === String(it.dbRecordRef))
            : null;
          setReeditCardsMed((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-med-${it.id}-${Date.now()}`, captured: it, medv2Record: record, initialPayloadOverride: mergedInitialPayload }, ...prev2]
          ));
          break;
        }
        case "chief_complaints": {
          // ZY-10387: resolve the backing CC v2 record from dbRecordRef so
          // the reedit card renders as an Update form (see render guard
          // at `captured.operation === "update" && ccv2Record` below).
          // Without this lookup the agent-triggered auto-reopen fell
          // through to the staged-add branch and showed a blank Add card.
          const record = it.operation === "update" && it.dbRecordRef
            ? (ccRows || []).find((r) => String(r.complaintId) === String(it.dbRecordRef))
            : null;
          setReeditCards((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-cc-${it.id}-${Date.now()}`, captured: it, ccv2Record: record, initialPayloadOverride: mergedInitialPayload }, ...prev2]
          ));
          break;
        }
        case "diagnosis": {
          // Resolve the backing Dx v2 row by dbRecordRef so the reedit
          // renders through the ccv2Record-style update branch (locked
          // name + ICD) when the AI merge is an operation=update. Null
          // dxv2Record would fall through to the staged-add path and
          // lose the lock. Mirrors the activity / allergies / FH /
          // chief_complaints lookup.
          const record = it.operation === "update" && it.dbRecordRef
            ? (dxRows || []).find((r) => String(r.diagnosisId) === String(it.dbRecordRef))
            : null;
          setReeditCardsDx((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-dx-${it.id}-${Date.now()}`, captured: it, dxv2Record: record, initialPayloadOverride: mergedInitialPayload }, ...prev2]
          ));
          break;
        }
        case "addictions": {
          // Resolve the backing Addiction v2 slot by payload.key so the
          // reedit renders through the DB-row-aware addiction overlay
          // (committed values stay visible on the card until the user
          // accepts the AI delta). Carries the staged payload too so
          // the form can seed from `effective` while the table keeps
          // showing lastCommitted (the user's prior save) — matches the
          // user's expectation that the TABLE only updates after the
          // reviewer clicks Update on the reedit card.
          const payloadIt = it.lastCommitted || it.current || {};
          const key = payloadIt.key;
          const record = key
            ? (addRows || []).find((r) => String(r.key || "").toLowerCase() === String(key).toLowerCase())
            : null;
          setReeditCardsAdd((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-add-${it.id}-${Date.now()}`, captured: it, addiction: record }, ...prev2]
          ));
          break;
        }
        case "anthropometric": {
          // Same wiring as Addictions — fixed-parameter resource, so
          // Shift-4 auto-pops a reedit card and the table waits on the
          // reviewer's accept before reflecting the AI delta.
          setReeditCardsAnth((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-anth-${it.id}-${Date.now()}`, captured: it }, ...prev2]
          ));
          break;
        }
        case "patient_details": {
          // Single-record UPDATE section — one captured per transcript
          // (uniqueKey "patient_details:main"). No per-card list to
          // target; the rail editor is already a single-instance
          // controlled component. Surface the rail so the reviewer
          // sees the AI delta merged over their committed state
          // (PatientDetailsEditor seeds from `effective`). Fired on
          // Shift-4 (AI merge onto an already-committed captured).
          // First-emit (pending state) is handled by the sibling
          // useEffect below — Shift-4 only sets reopenPending for
          // committed captures.
          openPatientDetailsRail();
          break;
        }
        default:
          break;
      }
      // ZY-10387 Shift 4: ack so the next poll doesn't re-pop the card.
      // Server returns 200 either way (idempotent); best-effort fire —
      // if it fails the user's next action on the card still mutates
      // the item and the subsequent response will reflect state.
      transcriptService.ackCapturedReopen(
        patientId, transcriptId, it.id,
        () => {}, () => {},
      );
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [items]);

  // Patient details — first-emit auto-open. The ZY-10387 reopen effect
  // above handles the Shift-4 case (AI merge onto a committed captured
  // → reopenPending=true → auto-pop rail). A brand-new AI emission
  // lands in `pending` state and never flips reopenPending — without
  // this effect the rail editor would stay closed and the reviewer
  // wouldn't see the AI delta at all (table reads from lastCommitted
  // only now).
  //
  // IMPORTANT: only trigger on `pending`. Including `added_in_call` /
  // `edited_in_call` here would re-open the rail immediately after the
  // reviewer clicks Update (committed captured still exists; the rail
  // just closed; the effect would snap it right back open) — infinite
  // reopen loop. Shift-4's reopenPending is the correct signal for
  // "AI changed something after your last commit"; this effect only
  // catches the first-emit case before any user commit.
  useEffect(() => {
    if (activeSection !== "patient_details") return;
    if (patientDetailsOpen) return;
    const hasPending = (items || []).some((it) => (
      it
      && it.targetSection === "patient_details"
      && !it.deletedAt
      && it.state === "pending"
    ));
    if (hasPending) {
      openPatientDetailsRail();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [items, activeSection, patientDetailsOpen]);

  // Food preferences — whole-doc PUT resource; AI captures flow
  // straight into the middle-column FoodPreference form via
  // FP_V2_AI_INJECT (see effect below). No right-rail card and no
  // re-edit state on this side any more.

  const [saving, setSaving] = useState({}); // cardId → bool
  const [errors, setErrors] = useState({}); // cardId → string

  // Med v2 doc (active + past) — loaded once so the Medicines
  // right-rail form can flag catalog picks that match a past entry
  // as "Already added". Reloads on MED_V2_REFRESH, which the Save &
  // next fan-out emits after it writes new rows to Med v2.
  const [medDoc, setMedDoc] = useState(null);
  useEffect(() => {
    if (!patientId) return undefined;
    let alive = true;
    const load = () => {
      new MedicineService().list(
        patientId,
        (res) => { if (alive) setMedDoc(res || { active: [], past: [] }); },
        () => { /* non-fatal — dedup falls back to the staged-row set */ },
      );
    };
    load();
    EventService.addListener(MED_V2_REFRESH, load);
    return () => {
      alive = false;
      EventService.removeListener(MED_V2_REFRESH, load);
    };
  }, [patientId]);

  // Per-section total of OPEN cards = AI proposals still awaiting a
  // decision + manually-opened editors (manual add / update / re-edit).
  // Bubbled up to the modal via onOpenCountsChange so the modal-level
  // SectionTabs strip shows the "● Label N" pill and the Review call
  // button can gate on "all counts zero". Food preferences intentionally
  // returns 0 here — that section is inline-edited via the middle-column
  // form (no rail-side card), so any AI proposals for it merge into the
  // form's own dirty state rather than counting as an open card.
  const openCountsBySection = useMemo(() => {
    const acc = {};
    (items || []).forEach((it) => {
      if (!it || it.state !== "pending") return;
      if (!it.targetSection) return;
      if (it.targetSection === "food_preferences") return;
      // Soft-deleted items don't render as cards (forSection drops
      // them) — counting them here would show a phantom "N" badge
      // with no card to open. Reported after a reject-with-blank-
      // content on an AI pending left the item at state=pending with
      // DeletedAt=now.
      if (it.deletedAt) return;
      acc[it.targetSection] = (acc[it.targetSection] || 0) + 1;
    });
    const bump = (section, n) => {
      if (!n) return;
      acc[section] = (acc[section] || 0) + n;
    };
    bump("chief_complaints",   manualCardIds.length    + updateCards.length    + reeditCards.length);
    bump("diagnosis",          manualCardIdsDx.length  + updateCardsDx.length  + reeditCardsDx.length);
    bump("medicines",          manualCardIdsMed.length + reeditCardsMed.length + updateCardsMed.length);
    bump("hospitalisation",    manualCardIdsHosp.length + reeditCardsHosp.length + updateCardsHosp.length);
    bump("lab_investigations", manualCardIdsLab.length + reeditCardsLab.length);
    bump("allergies",          manualCardIdsAlg.length + reeditCardsAlg.length + updateCardsAlg.length);
    bump("family_history",     manualCardIdsFh.length  + reeditCardsFh.length  + updateCardsFh.length);
    bump("addictions",         updateCardsAdd.length   + reeditCardsAdd.length);
    bump("activity",           manualCardIdsAct.length + reeditCardsAct.length + updateCardsAct.length);
    bump("anthropometric",     manualCardsAnth.length  + reeditCardsAnth.length);
    bump("daily_schedule",     reeditCardsDs.length);
    // drug_schedule: use MAX(pending-captured, live sheet count), not
    // their sum. The two sources overlap: when the editor is active
    // every pending captured opens a sheet (via DS_V2_EDIT_CAPTURED)
    // so dsSheetCount already includes them. When the editor hasn't
    // been visited yet, dsSheetCount is 0 but pending captures still
    // need the badge (the reviewer should see "Drug schedule 1" from
    // a different tab so they know to visit). Max covers both edges:
    //   editor closed, 2 pending → max(2, 0) = 2
    //   editor open, 2 pending → 2 sheets, max(2, 2) = 2
    //   editor open, 2 pending + 1 manual → 3 sheets, max(2, 3) = 3
    const dsPending = acc.drug_schedule || 0;
    acc.drug_schedule = Math.max(dsPending, dsSheetCount);
    // patient_details only ever has a single editor instance (one card
    // max — the body is one form, not a per-row stack), so this is
    // either 0 or 1. Omitting it meant the tab-strip badge never lit
    // up while the reviewer was mid-edit from the embedded table.
    bump("patient_details",    patientDetailsOpen ? 1 : 0);
    // food_preferences intentionally omitted (see comment above).
    return acc;
  }, [
    items,
    manualCardIds, updateCards, reeditCards,
    manualCardIdsDx, updateCardsDx, reeditCardsDx,
    manualCardIdsMed, reeditCardsMed, updateCardsMed,
    manualCardIdsHosp, reeditCardsHosp, updateCardsHosp,
    manualCardIdsLab, reeditCardsLab,
    manualCardIdsAlg, reeditCardsAlg, updateCardsAlg,
    manualCardIdsFh, reeditCardsFh, updateCardsFh,
    updateCardsAdd, reeditCardsAdd,
    manualCardIdsAct, reeditCardsAct, updateCardsAct,
    manualCardsAnth, reeditCardsAnth,
    reeditCardsDs,
    dsSheetCount,
    patientDetailsOpen,
  ]);

  // Fire the change callback whenever the counts shape changes. Guarded
  // via a ref so re-renders that produce equivalent counts don't cause
  // parent state churn (which would cascade back into props and loop).
  const lastOpenCountsRef = useRef("");
  useEffect(() => {
    const serialized = JSON.stringify(openCountsBySection);
    if (serialized === lastOpenCountsRef.current) return;
    lastOpenCountsRef.current = serialized;
    if (typeof onOpenCountsChange === "function") {
      onOpenCountsChange(openCountsBySection);
    }
  }, [openCountsBySection, onOpenCountsChange]);

  // Food-preferences special-case: no right-rail card is rendered for
  // this section (unlike every other capture). Instead, each new AI
  // food-preference proposal is forwarded to the middle-column
  // FoodPreference form via an event bus so it merges into the form's
  // local Likes/Dislikes/Restrictions/Limitations chip list — the
  // reviewer sees the AI's suggestion appear inline and hits the
  // form's own Save button when ready. Payload carries the item id
  // so the FoodPreference form can dedup by id and ignore re-emits
  // (fires on every items[] change and also on food-preferences tab
  // activation, so a late-mounting form catches proposals that landed
  // before it appeared).
  const emitFpInject = (it) => {
    EventService.emit(FP_V2_AI_INJECT, { id: it.id, ...(it.current || {}) });
  };
  useEffect(() => {
    (items || []).forEach((it) => {
      if (!it || it.state !== "pending") return;
      if (it.targetSection !== "food_preferences") return;
      emitFpInject(it);
    });
  }, [items]);
  useEffect(() => {
    if (activeSection !== "food_preferences") return;
    (items || []).forEach((it) => {
      if (!it || it.state !== "pending") return;
      if (it.targetSection !== "food_preferences") return;
      emitFpInject(it);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // Past-transcript (viewMode="review") click-to-open card. Reviewer
  // clicks a row's status chip in the SectionCaptureTable → we open
  // the same S5 card next to the table, populated from the captured
  // payload. "Save" in this card is view-only — the transcript is
  // already saved, so onSave just closes. Only one card at a time;
  // second click swaps.
  const [openedReviewId, setOpenedReviewId] = useState("");
  // Reset when the reviewer switches to another section so a stale
  // captured id from a different section doesn't try to render
  // through the wrong per-section form branch.
  useEffect(() => {
    setOpenedReviewId("");
  }, [activeSection]);

  // Listen for CC_V2_ADD + CC_V2_EDIT (fired from the embedded
  // ChiefComplaints's "+ Add complaints" toolbar and its per-row status
  // chip). Only intercepts when the Chief Complaints tab is active in
  // the modal; otherwise the sidebar's editor rail handles them.
  useEffect(() => {
    if (activeSection !== "chief_complaints") return undefined;
    const openManual = () => {
      setManualCardIds((prev) => {
        // Blank-card guard — matches ChiefComplaintEditor's sheet-stack
        // behaviour on the standalone page.
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [withPendingFocus(`m-${Date.now()}`), ...prev];
      });
    };
    const openUpdate = (payload) => {
      const c = payload && payload.complaint;
      if (!c || !c.complaintId) return;
      setUpdateCards((prev) => {
        // Don't stack two update sheets for the same complaint — first
        // wins, matches ComplaintForm's sheet-stack dup guard.
        if (prev.some((u) => u.complaint.complaintId === c.complaintId)) return prev;
        return [{ cardId: withPendingFocus(`u-${c.complaintId}-${Date.now()}`), complaint: c }, ...prev];
      });
    };
    const openReedit = (payload) => {
      const captured = payload && payload.captured;
      if (!captured || !captured.id) return;
      setReeditCards((prev) => {
        // One re-edit sheet per underlying captured item — clicking the
        // chip twice re-focuses the existing sheet rather than stacking.
        if (prev.some((r) => r.captured.id === captured.id)) return prev;
        return [
          { cardId: `r-${captured.id}`, captured, ccv2Record: (payload && payload.ccv2Record) || null },
          ...prev,
        ];
      });
    };
    EventService.addListener(CC_V2_ADD, openManual);
    EventService.addListener(CC_V2_EDIT, openUpdate);
    EventService.addListener(CC_V2_EDIT_CAPTURED, openReedit);
    return () => {
      EventService.removeListener(CC_V2_ADD, openManual);
      EventService.removeListener(CC_V2_EDIT, openUpdate);
      EventService.removeListener(CC_V2_EDIT_CAPTURED, openReedit);
    };
  }, [activeSection]);

  // Same pattern for the Diagnosis tab. Only intercepts when Diagnosis
  // is the active tab; standalone sidebar's DiagnosisEditor handles
  // DX_V2_ADD / DX_V2_STATUS the rest of the time.
  useEffect(() => {
    if (activeSection !== "diagnosis") return undefined;
    const openManual = () => {
      setManualCardIdsDx((prev) => {
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [withPendingFocus(`m-dx-${Date.now()}`), ...prev];
      });
    };
    const openUpdate = (payload) => {
      const d = payload && payload.diagnosis;
      if (!d || !d.diagnosisId) return;
      setUpdateCardsDx((prev) => {
        if (prev.some((u) => u.diagnosis.diagnosisId === d.diagnosisId)) return prev;
        return [{ cardId: withPendingFocus(`u-dx-${d.diagnosisId}-${Date.now()}`), diagnosis: d }, ...prev];
      });
    };
    const openReedit = (payload) => {
      const captured = payload && payload.captured;
      if (!captured || !captured.id) return;
      setReeditCardsDx((prev) => {
        if (prev.some((r) => r.captured.id === captured.id)) return prev;
        return [
          {
            cardId:     `r-dx-${captured.id}`,
            captured,
            dxv2Record: (payload && payload.dxv2Record) || null,
          },
          ...prev,
        ];
      });
    };
    EventService.addListener(DX_V2_ADD, openManual);
    EventService.addListener(DX_V2_STATUS, openUpdate);
    EventService.addListener(DX_V2_EDIT_CAPTURED, openReedit);
    return () => {
      EventService.removeListener(DX_V2_ADD, openManual);
      EventService.removeListener(DX_V2_STATUS, openUpdate);
      EventService.removeListener(DX_V2_EDIT_CAPTURED, openReedit);
    };
  }, [activeSection]);

  // Medicines tab — only listens for MED_V2_ADD (manual "+ Add medicine"
  // toolbar). MED_V2_STATUS / MED_V2_ADHERENCE / re-edit-captured events
  // are Wave A pass 2 (need MedicineStatusForm extension). Re-edit for
  // staged-add is opened via ChiefComplaints-style openUpdate branching
  // on the standalone Medicines component; that landing pad is deferred
  // until we add MED_V2_EDIT_CAPTURED to the events module.
  useEffect(() => {
    if (activeSection !== "medicines") return undefined;
    const openManual = () => {
      setManualCardIdsMed((prev) => {
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [withPendingFocus(`m-med-${Date.now()}`), ...prev];
      });
    };
    const openReedit = (payload) => {
      const captured = payload && payload.captured;
      if (!captured || !captured.id) return;
      setReeditCardsMed((prev) => {
        if (prev.some((r) => r.captured.id === captured.id)) return prev;
        return [{ cardId: `r-med-${captured.id}`, captured }, ...prev];
      });
    };
    // Medicine name / STATUS chip → open an update card that renders
    // MedicineStatusForm for that record. Dedup by medicineId so a
    // repeat click surfaces the existing card instead of stacking.
    //
    // ZY-10387 phase scope: medicine UPDATE flows (#5-#8) are
    // deliberately disabled in transcript mode. Row-click emits
    // MED_V2_STATUS but we no longer listen for it — the event is a
    // no-op in transcript mode. (Standalone orbit sidebar continues to
    // open MedicineStatusForm directly through its own listener.)
    // Re-enable by uncommenting the openUpdate handler + the listener
    // pair below.
    // const openUpdate = (payload) => { … setUpdateCardsMed(…); };
    EventService.addListener(MED_V2_ADD, openManual);
    EventService.addListener(MED_V2_EDIT_CAPTURED, openReedit);
    // EventService.addListener(MED_V2_STATUS, openUpdate);
    return () => {
      EventService.removeListener(MED_V2_ADD, openManual);
      EventService.removeListener(MED_V2_EDIT_CAPTURED, openReedit);
      // EventService.removeListener(MED_V2_STATUS, openUpdate);
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== "hospitalisation") return undefined;
    const openManual = () => {
      setManualCardIdsHosp((prev) => {
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [withPendingFocus(`m-hosp-${Date.now()}`), ...prev];
      });
    };
    const openReedit = (payload) => {
      const captured = payload && payload.captured;
      if (!captured || !captured.id) return;
      const hospv2Record = payload && payload.hospv2Record;
      setReeditCardsHosp((prev) => {
        if (prev.some((r) => r.captured.id === captured.id)) return prev;
        return [{ cardId: `r-hosp-${captured.id}`, captured, hospv2Record }, ...prev];
      });
    };
    const openUpdate = (payload) => {
      const h = payload && payload.hospitalisation;
      if (!h || !h.hospitalisationId) return;
      setUpdateCardsHosp((prev) => {
        if (prev.some((u) => u.record.hospitalisationId === h.hospitalisationId)) return prev;
        return [{ cardId: withPendingFocus(`u-hosp-${h.hospitalisationId}-${Date.now()}`), record: h }, ...prev];
      });
    };
    EventService.addListener(HOSP_V2_ADD, openManual);
    EventService.addListener(HOSP_V2_EDIT_CAPTURED, openReedit);
    EventService.addListener(HOSP_V2_EDIT, openUpdate);
    return () => {
      EventService.removeListener(HOSP_V2_ADD, openManual);
      EventService.removeListener(HOSP_V2_EDIT_CAPTURED, openReedit);
      EventService.removeListener(HOSP_V2_EDIT, openUpdate);
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== "lab_investigations") return undefined;
    // Lab uses one channel, LI_V2_EDIT, but the payload shape decides
    // what opens:
    //   • {captured} — the transcript LI table clicked an in-call
    //     captured row → open the row as a reedit DigitiseManuallyCard
    //     seeded from its payload.
    //   • {investigation} — legacy standalone row-click for the LI
    //     v2 editor rail, not the transcript flow. Still ignored here.
    //   • blank / {} — "+ Add investigation" or the empty-state CTA →
    //     push a new blank manual card into the rail.
    const openManual = (payload) => {
      if (payload && payload.captured) {
        const captured = payload.captured;
        const capturedId = captured.id || "";
        setReeditCardsLab((prev) => {
          // Dedup — clicking the same peach row twice re-focuses the
          // existing sheet instead of stacking a duplicate.
          if (prev.some((r) => r.captured && r.captured.id === capturedId)) return prev;
          return [{ cardId: withPendingFocus(`r-lab-${capturedId || Date.now()}`), captured }, ...prev];
        });
        return;
      }
      if (payload && payload.investigation) return; // legacy standalone row-click
      // Append (not prepend) so a new empty card lands BELOW the
      // one the reviewer just clicked "+ Add another test" from —
      // matches the "queue a sibling below" mental model. Blank-guard
      // uses the same cardFilledRef every other section uses; the card
      // now emits onFilledChange when it gains / loses a parent lab
      // test pick.
      setManualCardIdsLab((prev) => {
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [...prev, withPendingFocus(`m-lab-${Date.now()}`)];
      });
    };
    EventService.addListener(LI_V2_EDIT, openManual);
    return () => {
      EventService.removeListener(LI_V2_EDIT, openManual);
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== "allergies") return undefined;
    const openManual = () => {
      setManualCardIdsAlg((prev) => {
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [withPendingFocus(`m-alg-${Date.now()}`), ...prev];
      });
    };
    const openReedit = (payload) => {
      const captured = payload && payload.captured;
      if (!captured || !captured.id) return;
      const algv2Record = payload && payload.algv2Record;
      setReeditCardsAlg((prev) => {
        if (prev.some((r) => r.captured.id === captured.id)) return prev;
        return [{ cardId: `r-alg-${captured.id}`, captured, algv2Record }, ...prev];
      });
    };
    const openUpdate = (payload) => {
      const a = payload && payload.allergy;
      if (!a || !a.allergyId) return;
      setUpdateCardsAlg((prev) => {
        if (prev.some((u) => u.record.allergyId === a.allergyId)) return prev;
        return [{ cardId: withPendingFocus(`u-alg-${a.allergyId}-${Date.now()}`), record: a }, ...prev];
      });
    };
    EventService.addListener(ALG_V2_ADD, openManual);
    EventService.addListener(ALG_V2_EDIT_CAPTURED, openReedit);
    EventService.addListener(ALG_V2_EDIT, openUpdate);
    return () => {
      EventService.removeListener(ALG_V2_ADD, openManual);
      EventService.removeListener(ALG_V2_EDIT_CAPTURED, openReedit);
      EventService.removeListener(ALG_V2_EDIT, openUpdate);
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== "family_history") return undefined;
    const openManual = () => {
      setManualCardIdsFh((prev) => {
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [withPendingFocus(`m-fh-${Date.now()}`), ...prev];
      });
    };
    const openReedit = (payload) => {
      const captured = payload && payload.captured;
      if (!captured || !captured.id) return;
      const famv2Record = payload && payload.famv2Record;
      setReeditCardsFh((prev) => {
        if (prev.some((r) => r.captured.id === captured.id)) return prev;
        return [{ cardId: `r-fh-${captured.id}`, captured, famv2Record }, ...prev];
      });
    };
    const openUpdate = (payload) => {
      const r = payload && payload.relative;
      if (!r || !r.relativeId) return;
      setUpdateCardsFh((prev) => {
        if (prev.some((u) => u.record.relativeId === r.relativeId)) return prev;
        return [{ cardId: withPendingFocus(`u-fh-${r.relativeId}-${Date.now()}`), record: r }, ...prev];
      });
    };
    EventService.addListener(FAM_V2_ADD, openManual);
    EventService.addListener(FAM_V2_EDIT_CAPTURED, openReedit);
    EventService.addListener(FAM_V2_EDIT, openUpdate);
    return () => {
      EventService.removeListener(FAM_V2_ADD, openManual);
      EventService.removeListener(FAM_V2_EDIT_CAPTURED, openReedit);
      EventService.removeListener(FAM_V2_EDIT, openUpdate);
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== "addictions") return undefined;
    // The embedded Addictions component emits ADD_V2_EDIT with the row
    // the user clicked. Fixed 4-slot resource — no "add manually" event
    // — every card starts as an update of a real row. If a captured
    // item already exists for that addiction key (reviewer previously
    // staged tobacco/alcohol/etc. this call), open a REEDIT card
    // hydrated from captured.current. Otherwise open the regular
    // update card seeded from the DB row.
    const openUpdate = (payload) => {
      const a = payload && payload.addiction;
      if (!a || !a.key) return;
      const existing = (items || []).find((it) => {
        if (!it || it.targetSection !== "addictions") return false;
        if (it.deletedAt) return false;
        if (it.state !== "added_in_call" && it.state !== "edited_in_call" && it.state !== "rejected") return false;
        const payloadIt = it.lastCommitted || it.current || {};
        return payloadIt.key === a.key;
      });
      if (existing) {
        setReeditCardsAdd((prev) => {
          if (prev.some((r) => r.captured.id === existing.id)) return prev;
          // Carry the overlaid addiction row (`a` here is already the
          // Addictions.js effective-overlay row). AddictionForm uses it
          // as a secondary fallback — if the staged captured's effective
          // payload is somehow missing a field (bug, race, old server
          // build), the form still fills from the overlaid DB row's
          // previously-saved values rather than falling blank.
          return [{ cardId: `r-add-${existing.id}-${Date.now()}`, captured: existing, addiction: a }, ...prev];
        });
        return;
      }
      setUpdateCardsAdd((prev) => {
        if (prev.some((c) => c.addiction && c.addiction.key === a.key)) return prev;
        return [{ cardId: withPendingFocus(`u-add-${a.key}-${Date.now()}`), addiction: a }, ...prev];
      });
    };
    EventService.addListener(ADD_V2_EDIT, openUpdate);
    return () => {
      EventService.removeListener(ADD_V2_EDIT, openUpdate);
    };
  }, [activeSection, items]);

  useEffect(() => {
    if (activeSection !== "activity") return undefined;
    const openManual = () => {
      setManualCardIdsAct((prev) => {
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [withPendingFocus(`m-act-${Date.now()}`), ...prev];
      });
    };
    const openReedit = (payload) => {
      const captured = payload && payload.captured;
      if (!captured || !captured.id) return;
      const actv2Record = payload && payload.actv2Record;
      setReeditCardsAct((prev) => {
        if (prev.some((r) => r.captured.id === captured.id)) return prev;
        return [{ cardId: `r-act-${captured.id}`, captured, actv2Record }, ...prev];
      });
    };
    const openUpdate = (payload) => {
      const a = payload && payload.activity;
      if (!a || !a.activityId) return;
      setUpdateCardsAct((prev) => {
        if (prev.some((u) => u.record.activityId === a.activityId)) return prev;
        return [{ cardId: withPendingFocus(`u-act-${a.activityId}-${Date.now()}`), record: a }, ...prev];
      });
    };
    EventService.addListener(ACT_V2_ADD, openManual);
    EventService.addListener(ACT_V2_EDIT_CAPTURED, openReedit);
    EventService.addListener(ACT_V2_EDIT, openUpdate);
    EventService.addListener(ACT_V2_STATUS, openUpdate);
    return () => {
      EventService.removeListener(ACT_V2_ADD, openManual);
      EventService.removeListener(ACT_V2_EDIT_CAPTURED, openReedit);
      EventService.removeListener(ACT_V2_EDIT, openUpdate);
      EventService.removeListener(ACT_V2_STATUS, openUpdate);
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== "anthropometric") return undefined;
    // The embedded Anthropometric component emits
    // ANTH_V2_SELECT_PARAM with a parameter key when the user clicks a
    // row's name. If a captured item already exists for that parameter
    // (reviewer previously staged Height/Weight/etc. this call), open
    // a REEDIT card hydrated from captured.current. Otherwise open a
    // fresh manual card.
    const openManual = (key) => {
      if (typeof key !== "string" || !key) return;
      // BMI + WHR are server-derived — reject clicks on them if any.
      if (key === "bmi" || key === "whr") return;
      const existing = (items || []).find((it) => {
        if (!it || it.targetSection !== "anthropometric") return false;
        if (it.deletedAt) return false;
        if (it.state !== "added_in_call" && it.state !== "edited_in_call" && it.state !== "rejected") return false;
        const payload = it.lastCommitted || it.current || {};
        return payload.parameter === key;
      });
      if (existing) {
        setReeditCardsAnth((prev) => {
          if (prev.some((r) => r.captured.id === existing.id)) return prev;
          // Timestamped cardId so a fresh click after save gets a new
          // useCardDraft bucket. Matches the Addictions / PA fix.
          return [{ cardId: `r-anth-${existing.id}-${Date.now()}`, captured: existing }, ...prev];
        });
        return;
      }
      setManualCardsAnth((prev) => {
        if (prev.some((c) => c.parameter === key)) return prev;
        return [{ cardId: `m-anth-${key}-${Date.now()}`, parameter: key }, ...prev];
      });
    };
    EventService.addListener(ANTH_V2_SELECT_PARAM, openManual);
    return () => {
      EventService.removeListener(ANTH_V2_SELECT_PARAM, openManual);
    };
  }, [activeSection, items]);

  // Always-fresh version read for mutation `expectedVersion`. Reads
  // the modal's `latestVersionRef` first (bumped synchronously on
  // every poll + mutation response), falls back to the `version`
  // prop (React state, may lag one render). Fixes 409 conflicts
  // after a background poll bumped the doc but setSession hadn't
  // propagated yet.
  const latestVersion = () => {
    if (versionRef && typeof versionRef.current === "number" && versionRef.current > 0) {
      return versionRef.current;
    }
    return version || 0;
  };

  // Thin pass-through: the api-ai-transcript-v2-service now handles
  // version-conflict refetch-and-retry internally (service.saveWithRetry
  // in Go), so every mutating call that previously went through here
  // just needs to be fired with a fresh `latestVersion()`. Signature is
  // preserved to avoid rewriting the 13 call sites; the helper is now
  // just a sugar for "call this op with the current version."
  // Historical note: this used to wrap one refetch-and-retry on 409.
  // Removed when server-side retry landed — see ZY-10xxx.
  const mutateWithRetry = (op, onOk, onErr) => {
    op(latestVersion(), onOk || (() => {}), onErr || (() => {}));
  };

  const applyMutation = (resp) => {
    if (!resp || !resp.transcript) return;
    if (Array.isArray(resp.transcript.captured) && onItemsSync) {
      onItemsSync(resp.transcript.captured);
    }
    if (typeof resp.transcript.version === "number" && onVersionSync) {
      onVersionSync(resp.transcript.version);
    }
    // ZY-10356 Phase 6 — mutation responses carry the fresh
    // reviewedSections map (server-enriched with ReviewedByName). Pass
    // it up to the modal so the lockout banner + tab ✓ stay in sync
    // without waiting for the next poll tick. `omitempty` means the
    // key is absent when empty — default to `{}` so a stale prior
    // state doesn't linger.
    if (onReviewedSectionsSync) {
      const rs = resp.transcript.reviewedSections;
      onReviewedSectionsSync((rs && typeof rs === "object") ? rs : {});
    }
  };

  // AI card Save — updates the captured item's payload + moves it to
  // edited_in_call state. Server rejects invalid transitions (e.g. if
  // the item was already discarded); we surface that error inline.
  const saveAiCard = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    markSaveCommitted(item.id, payload);
    transcriptService.commitCaptured(
      patientId, transcriptId, item.id,
      { current: payload, expectedVersion: latestVersion() },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  // Manual card Save — creates a fresh captured item (state=proposed via
  // POST /captured) then immediately moves it to added_in_call. Two API
  // calls but reuses existing endpoints; confidence=0 is our "manual
  // source" heuristic until we add a proper Source field on the model.
  const saveManualCard = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "chief_complaints",
        operation:     "add",
        uniqueKey,
        current: payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        // Find the freshly-created item so we can advance its state.
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "chief_complaints" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIds((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIds((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCard = (cardId) => {
    setManualCardIds((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // ZY-10356 Phase 6 — manual-new Reject with content (design doc §3.1
  // row 2, col 1). Propose the captured item first (server stamps a
  // real captured.id), then immediately Reject it so the server sets
  // state=rejected + LastCommitted=current. The row surfaces on the
  // next poll with the strikethrough treatment and the chip label.
  // Blank form stays on the silent-local-drop path (cancelManualCard).
  const rejectManualCard = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "chief_complaints",
        operation:     "add",
        uniqueKey,
        current:       payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "chief_complaints" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIds((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.rejectCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (rejectResp) => {
            applyMutation(rejectResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIds((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not reject. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not reject. Try again.",
        }));
      },
    );
  };

  // Update card Save — stashes an "update" captured item on the
  // transcript. The review flow (Phase 4) picks these up + calls CC v2's
  // updateStatus on the referenced complaintId. In-call the item lives
  // only in the transcript buffer; the standalone ChiefComplaints table
  // still shows the untouched record.
  const saveUpdateCard = (cardId, complaint, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    // uniqueKey deliberately omits cardId — every "Update this complaint"
    // Save collapses onto the same captured item via Go's propose merge-
    // by-uniqueKey, so repeat clicks don't stack duplicate captures.
    const uniqueKey = `update:${complaint.complaintId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "chief_complaints",
        operation:     "update",
        dbRecordRef: String(complaint.complaintId),
        uniqueKey,
        current: payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "chief_complaints" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setUpdateCards((prev) => prev.filter((u) => u.cardId !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setUpdateCards((prev) => prev.filter((u) => u.cardId !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelUpdateCard = (cardId) => {
    setUpdateCards((prev) => prev.filter((u) => u.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // Shared propose+decide flow for update-card saves across every
  // section that materialises an update card off a middle-column row
  // click (Hosp / Alg / FH / PA). Same shape as CC's saveUpdateCard but
  // parameterised by section id, targetEntryId, and the setter for the
  // section's own updateCards state. Uses `update:<id>` as uniqueKey so
  // repeat clicks on the same row collapse onto one captured item on
  // the server side.
  const saveGenericUpdateCard = (
    cardId, targetSection, targetEntryId, payload, removeFromUpdateCards,
  ) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `update:${targetEntryId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection,
        operation:      "update",
        dbRecordRef:  String(targetEntryId),
        uniqueKey,
        current: payload,
        confidence:     0,
        evidence:       "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === targetSection && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          removeFromUpdateCards(cardId);
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            removeFromUpdateCards(cardId);
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const removeUpdateHosp = (cardId) =>
    setUpdateCardsHosp((prev) => prev.filter((u) => u.cardId !== cardId));
  const removeUpdateAlg = (cardId) =>
    setUpdateCardsAlg((prev) => prev.filter((u) => u.cardId !== cardId));
  const removeUpdateFh = (cardId) =>
    setUpdateCardsFh((prev) => prev.filter((u) => u.cardId !== cardId));
  const removeUpdateAct = (cardId) =>
    setUpdateCardsAct((prev) => prev.filter((u) => u.cardId !== cardId));

  const saveUpdateCardHosp = (cardId, record, payload) =>
    saveGenericUpdateCard(cardId, "hospitalisation", record.hospitalisationId, payload, removeUpdateHosp);
  const saveUpdateCardAlg  = (cardId, record, payload) =>
    saveGenericUpdateCard(cardId, "allergies",       record.allergyId,          payload, removeUpdateAlg);
  const saveUpdateCardFh   = (cardId, record, payload) =>
    saveGenericUpdateCard(cardId, "family_history",  record.relativeId,         payload, removeUpdateFh);
  const saveUpdateCardAct  = (cardId, record, payload) =>
    saveGenericUpdateCard(cardId, "activity",        record.activityId,         payload, removeUpdateAct);

  const cancelUpdateCardHosp = (cardId) => { removeUpdateHosp(cardId); setErrors((p) => ({ ...p, [cardId]: "" })); };
  const cancelUpdateCardAlg  = (cardId) => { removeUpdateAlg(cardId);  setErrors((p) => ({ ...p, [cardId]: "" })); };
  const cancelUpdateCardFh   = (cardId) => { removeUpdateFh(cardId);   setErrors((p) => ({ ...p, [cardId]: "" })); };
  const cancelUpdateCardAct  = (cardId) => { removeUpdateAct(cardId);  setErrors((p) => ({ ...p, [cardId]: "" })); };

  // Phase 3 reedit X-close → server-side revert (design doc §3.1). The
  // reedit card appears when AI deltas drifted Current away from the
  // reviewer's LastCommitted; closing it means "undo that drift" so
  // Current = LastCommitted again. Called from every section's
  // cancelReeditCard* handler so the server state matches the UI's
  // intent — otherwise a stale AI-delta Current would linger past the
  // next poll and re-open the reedit card.
  const revertAndRemoveReedit = (setList) => (cardId, captured) => {
    if (captured && captured.id) {
      mutateWithRetry(
        (v, ok, err) => transcriptService.revertCaptured(
          patientId, transcriptId, captured.id,
          { expectedVersion: v }, ok, err,
        ),
        (resp) => applyMutation(resp),
        () => {}, // silent — the UI already closed; server mismatch is rare and non-fatal
      );
    }
    setList((prev) => prev.filter((r) => r.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    // Drop the preserved form snapshot — next reopen must hydrate from
    // the committed captured.current (which revertCaptured just reset to
    // LastCommitted), NOT from the edits the reviewer just abandoned.
    clearCardDraft(cardId);
  };

  // ZY-10356 Phase 6 — reedit Reject (design doc §3.1 "Reject"). The
  // bottom Reject button on a re-opened card (#2, #4, #6, #8) is the
  // DESTRUCTIVE path (vs the X-close top-right which is SAFE revert).
  // For #2/#4 (new entries, no DBRecordRef) the server flips state to
  // rejected + stamps LastCommitted so the audit row surfaces with
  // strikethrough. For #6/#8 (DB-update rejects) the server soft-
  // deletes the captured item and the row reverts to the raw DB
  // record. Both branches close the card.
  const rejectAndRemoveReedit = (setList) => (cardId, captured) => {
    if (captured && captured.id) {
      mutateWithRetry(
        (v, ok, err) => transcriptService.rejectCaptured(
          patientId, transcriptId, captured.id,
          { expectedVersion: v }, ok, err,
        ),
        (resp) => applyMutation(resp),
        (err) => {
          setErrors((prev) => ({
            ...prev,
            [cardId]: (err && err.message) || "Could not reject. Try again.",
          }));
        },
      );
    }
    setList((prev) => prev.filter((r) => r.cardId !== cardId));
    // Same reason as revertAndRemoveReedit — if the rejected item is
    // revived by a later AI re-emission (server keeps the ID in the
    // state=rejected branch), the reedit must open from the fresh
    // captured.current, not the pre-reject edits.
    clearCardDraft(cardId);
  };

  // Re-edit save — updates the SAME captured item's current via
  // commitCaptured. Works for both operation="add" (staged-add rows
  // being tweaked) and operation="update" (staged-update rows being
  // tweaked). Server derives the terminal state from DBRecordRef so
  // the frontend doesn't need to specify.
  const saveReeditCard = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    markSaveCommitted(captured.id, payload);
    mutateWithRetry(
      (_v, _ok, _err) => transcriptService.commitCaptured(
        patientId, transcriptId, captured.id,
        { current: payload, expectedVersion: _v }, _ok, _err,
      ),
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCards((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCard = revertAndRemoveReedit(setReeditCards);
  const rejectReeditCard = rejectAndRemoveReedit(setReeditCards);

  // ─── Diagnosis save handlers — mirror the CC ones exactly, differing
  // only in the section slug on propose + which state array to clean.
  // Later refactor: one generic set of handlers taking `section` as arg.
  const saveAiCardDx = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    markSaveCommitted(item.id, payload);
    transcriptService.commitCaptured(
      patientId, transcriptId, item.id,
      { current: payload, expectedVersion: latestVersion() },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardDx = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "diagnosis",
        operation:     "add",
        uniqueKey,
        current: payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "diagnosis" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIdsDx((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIdsDx((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardDx = (cardId) => {
    setManualCardIdsDx((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveUpdateCardDx = (cardId, diagnosis, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `update:${diagnosis.diagnosisId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "diagnosis",
        operation:     "update",
        dbRecordRef: String(diagnosis.diagnosisId),
        uniqueKey,
        current: payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "diagnosis" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setUpdateCardsDx((prev) => prev.filter((u) => u.cardId !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setUpdateCardsDx((prev) => prev.filter((u) => u.cardId !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelUpdateCardDx = (cardId) => {
    setUpdateCardsDx((prev) => prev.filter((u) => u.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardDx = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    markSaveCommitted(captured.id, payload);
    mutateWithRetry(
      (_v, _ok, _err) => transcriptService.commitCaptured(
        patientId, transcriptId, captured.id,
        { current: payload, expectedVersion: _v }, _ok, _err,
      ),
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsDx((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardDx = revertAndRemoveReedit(setReeditCardsDx);
  const rejectReeditCardDx = rejectAndRemoveReedit(setReeditCardsDx);

  // ─── Medicines save handlers — Wave A first-cut adds only. Mirror
  // of the CC/Dx handlers with `targetSection: "medicines"`.
  const saveAiCardMed = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    markSaveCommitted(item.id, payload);
    transcriptService.commitCaptured(
      patientId, transcriptId, item.id,
      { current: payload, expectedVersion: latestVersion() },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardMed = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "medicines",
        operation:     "add",
        uniqueKey,
        current: payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "medicines" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIdsMed((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIdsMed((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardMed = (cardId) => {
    setManualCardIdsMed((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardMed = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    markSaveCommitted(captured.id, payload);
    mutateWithRetry(
      (_v, _ok, _err) => transcriptService.commitCaptured(
        patientId, transcriptId, captured.id,
        { current: payload, expectedVersion: _v }, _ok, _err,
      ),
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsMed((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardMed = revertAndRemoveReedit(setReeditCardsMed);
  const rejectReeditCardMed = rejectAndRemoveReedit(setReeditCardsMed);

  // ─── Hospitalisation save handlers — Wave A first-cut adds only.
  const saveAiCardHosp = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    markSaveCommitted(item.id, payload);
    transcriptService.commitCaptured(
      patientId, transcriptId, item.id,
      { current: payload, expectedVersion: latestVersion() },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardHosp = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "hospitalisation",
        operation:     "add",
        uniqueKey,
        current: payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "hospitalisation" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIdsHosp((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIdsHosp((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardHosp = (cardId) => {
    setManualCardIdsHosp((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardHosp = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    markSaveCommitted(captured.id, payload);
    mutateWithRetry(
      (_v, _ok, _err) => transcriptService.commitCaptured(
        patientId, transcriptId, captured.id,
        { current: payload, expectedVersion: _v }, _ok, _err,
      ),
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsHosp((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardHosp = revertAndRemoveReedit(setReeditCardsHosp);
  const rejectReeditCardHosp = rejectAndRemoveReedit(setReeditCardsHosp);

  // ─── Lab investigations save handlers — Wave A first-cut adds only.
  const saveAiCardLab = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    transcriptService.commitCaptured(
      patientId, transcriptId, item.id,
      { current: payload, expectedVersion: latestVersion() },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardLab = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "lab_investigations",
        operation:     "add",
        uniqueKey,
        current: payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "lab_investigations" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIdsLab((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIdsLab((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardLab = (cardId) => {
    setManualCardIdsLab((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardLab = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    mutateWithRetry(
      (_v, _ok, _err) => transcriptService.commitCaptured(
        patientId, transcriptId, captured.id,
        { current: payload, expectedVersion: _v }, _ok, _err,
      ),
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsLab((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardLab = revertAndRemoveReedit(setReeditCardsLab);
  const rejectReeditCardLab = rejectAndRemoveReedit(setReeditCardsLab);

  // ─── Allergies save handlers — Wave B first-cut adds only.
  const saveAiCardAlg = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    markSaveCommitted(item.id, payload);
    transcriptService.commitCaptured(
      patientId, transcriptId, item.id,
      { current: payload, expectedVersion: latestVersion() },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardAlg = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "allergies",
        operation:     "add",
        uniqueKey,
        current: payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "allergies" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIdsAlg((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIdsAlg((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardAlg = (cardId) => {
    setManualCardIdsAlg((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardAlg = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    markSaveCommitted(captured.id, payload);
    mutateWithRetry(
      (_v, _ok, _err) => transcriptService.commitCaptured(
        patientId, transcriptId, captured.id,
        { current: payload, expectedVersion: _v }, _ok, _err,
      ),
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsAlg((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardAlg = revertAndRemoveReedit(setReeditCardsAlg);
  const rejectReeditCardAlg = rejectAndRemoveReedit(setReeditCardsAlg);

  // ─── Family history save handlers — Wave B first-cut adds only.
  const saveAiCardFh = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    markSaveCommitted(item.id, payload);
    transcriptService.commitCaptured(
      patientId, transcriptId, item.id,
      { current: payload, expectedVersion: latestVersion() },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardFh = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "family_history",
        operation:     "add",
        uniqueKey,
        current: payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "family_history" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIdsFh((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIdsFh((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardFh = (cardId) => {
    setManualCardIdsFh((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardFh = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    markSaveCommitted(captured.id, payload);
    mutateWithRetry(
      (_v, _ok, _err) => transcriptService.commitCaptured(
        patientId, transcriptId, captured.id,
        { current: payload, expectedVersion: _v }, _ok, _err,
      ),
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsFh((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardFh = revertAndRemoveReedit(setReeditCardsFh);
  const rejectReeditCardFh = rejectAndRemoveReedit(setReeditCardsFh);

  // ─── Addictions save handlers — Wave B first-cut. Every save is an
  //     "add" op on the transcript (backend rewrites the slot on every
  //     write; there's no distinct update path for the fixed 4-slot
  //     addictions resource).
  const saveAiCardAdd = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    transcriptService.commitCaptured(
      patientId, transcriptId, item.id,
      { current: payload, expectedVersion: latestVersion() },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  // Update-flow save (from the row-name / chip-click card). Same wire
  // as saveManualCardFh but tagged section=addictions + dedup key
  // baked from the addiction.key so a second click on the same row
  // reuses the same captured item.
  const saveUpdateCardAdd = (cardId, addiction, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `addiction:${addiction.key}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "addictions",
        operation:     "add",
        uniqueKey,
        current: payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "addictions" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setUpdateCardsAdd((prev) => prev.filter((c) => c.cardId !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setUpdateCardsAdd((prev) => prev.filter((c) => c.cardId !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelUpdateCardAdd = (cardId) => {
    setUpdateCardsAdd((prev) => prev.filter((c) => c.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardAdd = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    mutateWithRetry(
      (_v, _ok, _err) => transcriptService.commitCaptured(
        patientId, transcriptId, captured.id,
        { current: payload, expectedVersion: _v }, _ok, _err,
      ),
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsAdd((prev) => prev.filter((r) => r.cardId !== cardId));
        // Drop the preserved form snapshot so the next AI re-emit on
        // the same captured hydrates its reedit card from the fresh
        // effective payload (new AI delta on top of just-committed LC)
        // instead of replaying the just-saved field values. Without
        // this, a reopen for "4 days" would still show "2 days" from
        // the stale draft.
        clearCardDraft(cardId);
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardAdd = revertAndRemoveReedit(setReeditCardsAdd);
  const rejectReeditCardAdd = rejectAndRemoveReedit(setReeditCardsAdd);

  // ─── Physical activity save handlers — Wave B first-cut adds only.
  const saveAiCardAct = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    markSaveCommitted(item.id, payload);
    transcriptService.commitCaptured(
      patientId, transcriptId, item.id,
      { current: payload, expectedVersion: latestVersion() },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardAct = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "activity",
        operation:     "add",
        uniqueKey,
        current: payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "activity" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIdsAct((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIdsAct((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardAct = (cardId) => {
    setManualCardIdsAct((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardAct = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    markSaveCommitted(captured.id, payload);
    mutateWithRetry(
      (_v, _ok, _err) => transcriptService.commitCaptured(
        patientId, transcriptId, captured.id,
        { current: payload, expectedVersion: _v }, _ok, _err,
      ),
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsAct((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardAct = revertAndRemoveReedit(setReeditCardsAct);
  const rejectReeditCardAct = rejectAndRemoveReedit(setReeditCardsAct);

  // ─── Anthropometric save handlers — Wave C first-cut. Every save is
  //     an "add" op on the transcript (backend derives Add vs
  //     Correction based on prior history; we just POST to the same
  //     endpoint on either path).
  const saveAiCardAnth = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    transcriptService.commitCaptured(
      patientId, transcriptId, item.id,
      { current: payload, expectedVersion: latestVersion() },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardAnth = (cardId, parameter, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `anthropometric:${parameter}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "anthropometric",
        operation:     "add",
        uniqueKey,
        current: payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "anthropometric" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardsAnth((prev) => prev.filter((c) => c.cardId !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardsAnth((prev) => prev.filter((c) => c.cardId !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardAnth = (cardId) => {
    setManualCardsAnth((prev) => prev.filter((c) => c.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardAnth = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    mutateWithRetry(
      (_v, _ok, _err) => transcriptService.commitCaptured(
        patientId, transcriptId, captured.id,
        { current: payload, expectedVersion: _v }, _ok, _err,
      ),
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsAnth((prev) => prev.filter((r) => r.cardId !== cardId));
        // Drop the preserved form snapshot so a subsequent reopen of
        // the same captured hydrates from the fresh effective payload
        // instead of replaying the just-saved field values.
        clearCardDraft(cardId);
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardAnth = revertAndRemoveReedit(setReeditCardsAnth);
  const rejectReeditCardAnth = rejectAndRemoveReedit(setReeditCardsAnth);

  // ─── Daily schedule save handlers — Wave C first-cut adds only.
  const saveAiCardDs = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    transcriptService.commitCaptured(
      patientId, transcriptId, item.id,
      { current: payload, expectedVersion: latestVersion() },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveReeditCardDs = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    mutateWithRetry(
      (_v, _ok, _err) => transcriptService.commitCaptured(
        patientId, transcriptId, captured.id,
        { current: payload, expectedVersion: _v }, _ok, _err,
      ),
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsDs((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardDs = revertAndRemoveReedit(setReeditCardsDs);
  const rejectReeditCardDs = rejectAndRemoveReedit(setReeditCardsDs);

  // Drug schedule manual add — the middle-column DrugSchedule's
  // "+ Add schedule" fires DS_V2_ADD, useSheetStack in DrugScheduleEditor
  // pushes a sheet, the sheet calls this handler on Save. We do the
  // propose+decide dance so the plan lands in /captured with
  // state=added_in_call — Review Save & next then fans it out via the
  // transcript service's writeDrugSchedule (which owns the pharmacy
  // POST). No manualCardIds tracking here because the sheet lifecycle
  // is entirely inside useSheetStack; success/error come back via
  // the callback pair the sheet passes in.
  // Mirror of the Python agent's `_slug` helper (lowercase + collapse
  // non-alphanumerics to '-' + strip). Must stay in sync — the AI's
  // propose uses this to form `drug_schedule:<slug(drug)>`, and when
  // the reviewer's reject/save goes through a *different* slug the
  // server treats them as separate uniqueKeys, so the AI's original
  // captured stays in pending and the section-header count never
  // drops. Previously this was just `.toLowerCase().trim()`, which
  // left spaces/punctuation intact — "Paracetamol 500mg" became
  // "paracetamol 500mg" on this side vs "paracetamol-500mg" on the
  // agent side, and reject never merged.
  const drugUniqueSlug = (name) => {
    const s = String(name || "").trim().toLowerCase();
    if (!s) return "";
    return s.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  };
  const saveManualCardDrug = (body, cbs) => {
    // ZY-10356 Phase 2 — content-derived uniqueKey so a second manual
    // add for the same drug merges into the existing captured item
    // instead of creating a duplicate. Mirrors the per-section natural-
    // key convention (medicines keyed on name here since drug schedule
    // has no mCode); normalized via `drugUniqueSlug` to match the
    // Python agent's _slug. Falls back to a timestamp when the body
    // has no drug name — same safety net the old code implicitly had.
    const payload = drugScheduleBodyToCapturedAdd(body);
    const drugSlug = drugUniqueSlug(payload.drug_name);
    const uniqueKey = drugSlug
      ? `drug_schedule:${drugSlug}`
      : `manual:drug-${Date.now()}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection:   "drug_schedule",
        operation:       "add",
        uniqueKey,
        current: payload,
        confidence:      0,
        evidence:        "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "drug_schedule" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        // ZY-10312 — mark the new captured id as already-handled BEFORE
        // applyMutation triggers a re-render. The propose response puts
        // the item in state=pending momentarily (until commitCaptured
        // flips it to added_in_call), and the auto-open effect at
        // openedDrugProposalsRef below would otherwise interpret that
        // pending window as a fresh AI proposal and emit
        // DS_V2_EDIT_CAPTURED — spawning a duplicate AddSheet beside
        // the one the reviewer just saved from.
        if (created) openedDrugProposalsRef.current.add(created.id);
        applyMutation(proposeResp);
        if (!created) {
          if (cbs && cbs.onSuccess) cbs.onSuccess();
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            if (cbs && cbs.onSuccess) cbs.onSuccess();
          },
          (err) => { if (cbs && cbs.onError) cbs.onError(err); },
        );
      },
      (err) => { if (cbs && cbs.onError) cbs.onError(err); },
    );
  };

  // ZY-10312 — Reject-with-content for a manual DS Add sheet. Mirrors
  // CC's rejectManualCard: propose the body THEN flip to rejected so
  // the audit row surfaces with a strikethrough instead of being lost
  // entirely to a local sheet-drop (which is what plain Reject-as-
  // Cancel would do). Same auto-open guard as saveManualCardDrug so
  // the propose→reject round-trip can't retrigger DS_V2_EDIT_CAPTURED.
  const rejectManualCardDrug = (body, cbs) => {
    const payload = drugScheduleBodyToCapturedAdd(body);
    const drugSlug = drugUniqueSlug(payload.drug_name);
    const uniqueKey = drugSlug
      ? `drug_schedule:${drugSlug}`
      : `manual:drug-${Date.now()}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection:   "drug_schedule",
        operation:       "add",
        uniqueKey,
        current: payload,
        confidence:      0,
        evidence:        "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "drug_schedule" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (created) openedDrugProposalsRef.current.add(created.id);
        applyMutation(proposeResp);
        if (!created) {
          if (cbs && cbs.onSuccess) cbs.onSuccess();
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        transcriptService.rejectCaptured(
          patientId, transcriptId, created.id,
          { expectedVersion: nextVersion },
          (rejectResp) => {
            applyMutation(rejectResp);
            if (cbs && cbs.onSuccess) cbs.onSuccess();
          },
          (err) => { if (cbs && cbs.onError) cbs.onError(err); },
        );
      },
      (err) => { if (cbs && cbs.onError) cbs.onError(err); },
    );
  };

  // Drug schedule save — matches CC's saveAiCard: always
  // edited_in_call, regardless of whether the source was a `proposed`
  // AI capture the reviewer is accepting or an `added_in_call` staged
  // plan being re-edited. The Go state machine accepts both
  // transitions, and using one state keeps the rest of the pipeline
  // (Review & Save filter, tab counters) consistent across sections.
  const updateManualCardDrug = (capturedId, body, cbs) => {
    const payload = drugScheduleBodyToCapturedAdd(body);
    transcriptService.commitCaptured(
      patientId, transcriptId, capturedId,
      { current: payload, expectedVersion: latestVersion() },
      (resp) => {
        applyMutation(resp);
        if (cbs && cbs.onSuccess) cbs.onSuccess();
      },
      (err) => { if (cbs && cbs.onError) cbs.onError(err); },
    );
  };

  // Drug schedule AI proposals — each new `proposed` capture auto-opens
  // an is-ai themed AddSheet in the DrugScheduleEditor rail (via
  // DS_V2_EDIT_CAPTURED with a plan-shape wrapping the payload). The
  // reviewer edits fields as needed and clicks Add; updateManualCardDrug
  // above transitions the state to added_in_call, at which point the
  // item appears in the middle-column plan table as a peach row.
  //
  // Track already-auto-opened proposal ids in a ref so a re-render or
  // an unrelated `items` update doesn't spawn a duplicate sheet. Ids
  // are never removed — once opened, always considered handled. If the
  // reviewer cancels the sheet without clicking Add, the proposal
  // stays in state=proposed and surfaces on Review & save.
  const openedDrugProposalsRef = useRef(new Set());
  // Track the hash of each opened proposal so a mid-review AI re-emit
  // (same captured.id, new captured.current) force-remounts its sheet
  // with the fresh payload. Mirrors the Addictions / Anthropometric
  // scoped-formId pattern — the editor's sheet state is seeded once
  // from the plan prop on mount, so a plain re-fire of the SAME plan
  // id would not refresh the AddSheet's useState fields.
  const openedDrugPayloadHashRef = useRef(new Map());
  const _dsPayloadHash = (it) => {
    try { return JSON.stringify(it.effective || it.current || {}); }
    catch (e) { return ""; }
  };
  useEffect(() => {
    // DrugScheduleEditor is only mounted while drug_schedule is the
    // active tab (CapturedSections gates the right-column render).
    // Firing DS_V2_EDIT_CAPTURED when the editor isn't listening would
    // silently drop the sheet, so we gate on the same condition and
    // let this effect re-run on tab activation — any proposals that
    // arrived while another tab was active auto-open on switch back.
    if (activeSection !== "drug_schedule") return;
    if (!Array.isArray(items) || items.length === 0) return;
    for (const it of items) {
      if (!it || it.targetSection !== "drug_schedule") continue;
      // Three triggers for a sheet open:
      //   (a) first-time pending proposal that we haven't opened yet.
      //   (b) ZY-10387 Shift-4 reopenPending: AI merged a new payload
      //       onto an already-committed captured — pop the sheet so
      //       the reviewer can re-review the overlaid plan.
      //   (c) same captured.id as a sheet we already opened, but the
      //       payload hash changed (AI re-emit on a still-open sheet).
      //       Treat as a re-fire so the AddSheet remounts with fresh
      //       state.
      const isPending = it.state === "pending";
      const isShift4  = !!it.reopenPending
        && (it.state === "added_in_call" || it.state === "edited_in_call");
      const hash      = _dsPayloadHash(it);
      const already   = openedDrugProposalsRef.current.has(it.id);
      const payloadChanged = openedDrugPayloadHashRef.current.get(it.id) !== hash;
      const shouldOpen = (!already && (isPending || isShift4))
        || (already && (isShift4 || payloadChanged));
      if (!shouldOpen) continue;
      openedDrugProposalsRef.current.add(it.id);
      openedDrugPayloadHashRef.current.set(it.id, hash);
      // dsStagedAddToPlan handles the "AI captured atoms but no
      // expanded doses[] yet" case by synthesising doses from
      // drug_name / start_date / num_doses / interval_value /
      // interval_unit — same math the Go adapter runs on commit.
      // Without this the AddSheet seed sees an empty doses[] and
      // can't populate the drug picker or dose plan.
      const plan = dsStagedAddToPlan(it);
      EventService.emit(DS_V2_EDIT_CAPTURED, { plan, patient });
      if (isShift4) {
        // Ack the reopen flag so the next poll doesn't re-pop. Best-
        // effort; the sheet is already open and the ack is idempotent
        // server-side.
        transcriptService.ackCapturedReopen(
          patientId, transcriptId, it.id,
          () => {}, () => {},
        );
      }
    }
  }, [activeSection, items, patient, patientId, transcriptId, transcriptService]);

  // Patient details manual card save. Single-record UPDATE section:
  // one and only one captured item ever exists per transcript for
  // patient_details (uniqueKey is fixed at "patient_details:main" so
  // the Go proposeCaptured handler upserts onto the same row every
  // time — a subsequent manual save or AI capture merges into it,
  // never appends). No cardId tracking here because the rail is a
  // single-instance editor driven by PD_V2_EDIT + PD_V2_CLOSE, not
  // useSheetStack.
  //
  // State-transition subtlety unique to this section: because we upsert
  // onto the same row, the item's state after the FIRST save is
  // added_in_call. The Go state machine forbids added_in_call →
  // added_in_call, so subsequent saves must route to edited_in_call
  // instead (legal from both proposed and added_in_call). The rule:
  //   from `proposed`                          → added_in_call
  //   from `added_in_call` / `edited_in_call`  → edited_in_call
  const saveManualCardPatient = (body, cbs) => {
    const uniqueKey = "patient_details:main";
    const payload = patientDetailsBodyToCapturedUpdate(body);
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection:   "patient_details",
        operation:       "update",
        uniqueKey,
        current: payload,
        confidence:      0,
        evidence:        "",
        expectedVersion: latestVersion(),
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "patient_details" && it.uniqueKey === uniqueKey && !it.deletedAt,
        );
        if (!created) {
          if (cbs && cbs.onSuccess) cbs.onSuccess();
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || latestVersion();
        // Pick the transition based on the item's current state
        // (added_in_call → added_in_call is illegal on the Go side).
        const newState =
          created.state === "added_in_call" || created.state === "edited_in_call"
            ? "edited_in_call"
            : "added_in_call";
        transcriptService.commitCaptured(
          patientId, transcriptId, created.id,
          { newState, expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            // Close the rail directly here. Can't rely solely on the
            // form's cbs.onSuccess path: PatientDetailsEditor keys its
            // PatientDetailsForm on a payload hash so that mid-review
            // AI re-emits remount the open card. applyMutation above
            // ships the committed captured — its new content flips the
            // hash → React unmounts the form instance → the form's
            // onSuccess closure sees `mounted.current === false` and
            // bails before calling onSaved → the rail would stay open.
            // Owning the close here sidesteps the race.
            closePatientDetailsRail();
            if (cbs && cbs.onSuccess) cbs.onSuccess();
          },
          (err) => { if (cbs && cbs.onError) cbs.onError(err); },
        );
      },
      (err) => { if (cbs && cbs.onError) cbs.onError(err); },
    );
  };

  // Food-preference save/re-edit handlers were removed with the
  // right-rail card — captures merge into the middle-column form via
  // FP_V2_AI_INJECT and persist through the form's own Save button.
  // Save-and-next at Review time still commits any pending proposals
  // via the existing SaveSection fan-out.

  // Cancel on an AI card = Reject (ZY-10356 Phase 3). Server soft-deletes
  // DB-update captured items and sets state=rejected (with LastCommitted
  // snapshot for audit) on new-entry items with content. From the UI's
  // perspective both branches "remove the proposal from the live column";
  // the audit distinction is server-side only.
  const discardAi = (item) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    transcriptService.rejectCaptured(
      patientId, transcriptId, item.id,
      { expectedVersion: latestVersion() },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not discard.",
        }));
      },
    );
  };

  // AI-proposal dedup save: an AI proposal whose name matches an
  // existing patient row is rendered as an update card on that record
  // instead of a duplicate add. On save we (a) discard the original
  // ADD proposal so it doesn't linger, then (b) propose+decide a fresh
  // UPDATE captured item bound to the matched record. Same propose+
  // decide flow saveGenericUpdateCard already uses.
  //
  // Dedup convention: reuse the AI's ORIGINAL uniqueKey when creating
  // the staged update (not `update:${targetEntryId}`). The AI agent
  // emits the same uniqueKey across re-emissions for the same clinical
  // entity (see zyla-transcript-agent `_captured_unique_key`); keeping
  // that key here means a later AI re-emission MERGES into our staged
  // update (server match-by-uniqueKey) and triggers the auto-reopen
  // effect, instead of creating a parallel X3 that renders as a second
  // card for the same row.
  //
  // Source override: pass `source: "ai"` so the server stamps the new
  // captured as source=ai (otherwise the actor-derived default of
  // "manual" would flip the row's chip theme to indigo).
  const saveAiProposalAsUpdate = (
    item, targetSection, targetEntryId, payload, onSuccess, onFailure,
  ) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    const uniqueKey = item.uniqueKey || `update:${targetEntryId}`;
    transcriptService.rejectCaptured(
      patientId, transcriptId, item.id,
      { expectedVersion: latestVersion() },
      (discardResp) => {
        applyMutation(discardResp);
        const nextV1 =
          (discardResp.transcript && discardResp.transcript.version) || latestVersion();
        transcriptService.proposeCaptured(
          patientId, transcriptId,
          {
            targetSection,
            operation:      "update",
            dbRecordRef:  String(targetEntryId),
            uniqueKey,
            source:        "ai",
            current: payload,
            confidence:     item.confidence || 0,
            evidence:       item.evidence || "",
            expectedVersion: nextV1,
          },
          (proposeResp) => {
            applyMutation(proposeResp);
            const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
            const created = (fresh || []).find(
              (c) => c.targetSection === targetSection && c.uniqueKey === uniqueKey && !c.deletedAt,
            );
            if (!created) {
              setSaving((prev) => ({ ...prev, [item.id]: false }));
              if (onSuccess) onSuccess();
              return;
            }
            const nextV2 =
              (proposeResp.transcript && proposeResp.transcript.version) || nextV1;
            transcriptService.commitCaptured(
              patientId, transcriptId, created.id,
              { expectedVersion: nextV2 },
              (decideResp) => {
                applyMutation(decideResp);
                setSaving((prev) => ({ ...prev, [item.id]: false }));
                if (onSuccess) onSuccess();
              },
              (err) => {
                setSaving((prev) => ({ ...prev, [item.id]: false }));
                setErrors((prev) => ({
                  ...prev,
                  [item.id]: (err && err.message) || "Could not finalise. Try again.",
                }));
                if (onFailure) onFailure(err);
              },
            );
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [item.id]: false }));
            setErrors((prev) => ({
              ...prev,
              [item.id]: (err && err.message) || "Could not save. Try again.",
            }));
            if (onFailure) onFailure(err);
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not discard. Try again.",
        }));
        if (onFailure) onFailure(err);
      },
    );
  };

  // Soft-deleted items must never surface as actionable cards. Backend
  // RejectCaptured sets `DeletedAt` on blank new-entries and DB-update
  // rejects but intentionally leaves `state` untouched (service/transcript.go:
  // RejectCaptured default branch). So a soft-deleted item can still carry
  // state="pending" / "added_in_call" / "edited_in_call" and would otherwise
  // fall into the `proposals` or `addedRows` buckets below, where the
  // reviewer clicks Add/Reject and the server rightfully returns
  // ErrCapturedItemDeleted. Drop them at the top-level section filter so
  // every downstream computation (proposals, addedRows, review table,
  // overlays into standalone sections) sees a clean working set.
  const forSection = activeSection
    ? items.filter((it) => it.targetSection === activeSection && !it.deletedAt)
    : [];
  const addedRows  = forSection.filter(
    (it) => it.state === "added_in_call" || it.state === "edited_in_call",
  );
  const proposals  = forSection.filter((it) => it.state === "pending");

  // Medicines — mCodes already represented in the left-side ACTIVE
  // table. Union of currently-active Med v2 rows and in-call captures
  // that landed as "added", "edited", or new-entry "rejected" rows.
  // Past medicines are intentionally NOT in this set — the patient
  // can be re-prescribed a drug they completed earlier, so the
  // catalog picker must allow re-selecting a past entry's mCode.
  // Pending AI proposals also aren't counted — they live on the
  // right rail until the reviewer saves or rejects them.
  // ZY-10312 — "already added" overlay for the CC / Dx / PA search
  // dropdowns in TRANSCRIPT mode. The forms already fetch the DB
  // active list themselves (ChiefComplaintsService.list etc.), but
  // items staged earlier in this same call aren't yet persisted, so
  // the picker would otherwise let a reviewer stage the same complaint
  // / diagnosis / activity twice. CapturedSections owns the staged
  // state, so it builds the overlay here and passes down.
  //
  // Shape mirrors medBlockedMcodes: only COMMITTED captures (add op in
  // added_in_call / edited_in_call) + new-entry rejects with content
  // land in the set — pending AI proposals aren't counted (they live
  // on the right rail until save/reject). AI-pill click paths that
  // generate a search also benefit; standalone callers are untouched
  // because they never see this prop.
  const stagedCcIcdCodes = useMemo(() => {
    const s = new Set();
    for (const it of items || []) {
      if (!it || it.targetSection !== "chief_complaints") continue;
      if ((it.operation || "add") !== "add") continue;
      const isCommitted = it.state === "added_in_call" || it.state === "edited_in_call";
      const isNewReject = it.state === "rejected" && !it.dbRecordRef && it.lastCommitted;
      if (!isCommitted && !isNewReject) continue;
      const payload = it.lastCommitted || it.current || {};
      const code = String(payload.icd_code || "").trim().toLowerCase();
      if (code) s.add(code);
    }
    return s;
  }, [items]);

  const stagedDxIcdCodes = useMemo(() => {
    const s = new Set();
    for (const it of items || []) {
      if (!it || it.targetSection !== "diagnosis") continue;
      if ((it.operation || "add") !== "add") continue;
      const isCommitted = it.state === "added_in_call" || it.state === "edited_in_call";
      const isNewReject = it.state === "rejected" && !it.dbRecordRef && it.lastCommitted;
      if (!isCommitted && !isNewReject) continue;
      const payload = it.lastCommitted || it.current || {};
      const code = String(payload.icd_code || "").trim().toLowerCase();
      if (code) s.add(code);
    }
    return s;
  }, [items]);

  const stagedActivityNames = useMemo(() => {
    const s = new Set();
    for (const it of items || []) {
      // PA's targetSection on the wire is "activity" (NOT
      // "physical_activity" — that's only the UI tab slug). The
      // propose-captured call in saveManualCardAct / saveReeditCardAct
      // writes "activity"; matching anything else here would silently
      // return an empty set.
      if (!it || it.targetSection !== "activity") continue;
      if ((it.operation || "add") !== "add") continue;
      const isCommitted = it.state === "added_in_call" || it.state === "edited_in_call";
      const isNewReject = it.state === "rejected" && !it.dbRecordRef && it.lastCommitted;
      if (!isCommitted && !isNewReject) continue;
      const payload = it.lastCommitted || it.current || {};
      const name = String(payload.name || "").trim().toLowerCase();
      if (name) s.add(name);
    }
    return s;
  }, [items]);

  // Note: ZY-10388's earlier client-side `algStagedClaims` + dedup
  // effect lived here and silently rejected AI re-emissions whose
  // substance matched an in-call-committed captured. That approach
  // was superseded by the backend fix (allergy UniqueKey is now
  // substance-only, and CommitCaptured rewrites UniqueKey + stashes
  // the prior key on RewrittenFrom so ProposeCaptured's merge walk
  // collapses either shape into the existing captured). The server
  // now triggers Shift-4 ReopenPending for the duplicate on its own,
  // matching how CC/Dx handle re-emissions of a committed identity.
  // Rolled back here to keep that server flow as the single source
  // of truth; the FE no longer needs to second-guess it.

  // Family history — single-only relationships (Father / Mother / 4
  // grandparents) that have been STAGED this call but not yet persisted
  // to Family-History v2. RelativeForm's own `takenSingles` set reads
  // the DB-persisted list via its JIT loadList; it's blind to captures
  // that are only `added_in_call`. Each ADD card gets a `siblingClaims`
  // list derived from this set MINUS the card's own captured
  // relationship (so a reedit on Father never flags itself). Multi
  // relationships (brother / sister / other) are legitimate repeats
  // and are intentionally omitted. See the per-card helper below.
  const fhStagedSingleClaims = useMemo(() => {
    const out = [];
    for (const it of items || []) {
      if (!it || it.targetSection !== "family_history") continue;
      if (it.deletedAt) continue;
      const isCommitted = it.state === "added_in_call" || it.state === "edited_in_call";
      const isNewReject = it.state === "rejected" && !it.dbRecordRef && it.lastCommitted;
      if (!isCommitted && !isNewReject) continue;
      const p = it.effective || it.lastCommitted || it.current || {};
      const raw = (p.relationship || p.value || "").toString();
      const relKey = raw.trim().toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");
      if (relKey && isSingleRelationship(relKey)) {
        out.push({ capturedId: String(it.id || ""), relKey });
      }
    }
    return out;
  }, [items]);

  const medBlockedMcodes = useMemo(() => {
    const s = new Set();
    ((medDoc && medDoc.active) || []).forEach((m) => {
      const c = m && m.medicineCode && m.medicineCode.mCode;
      if (c) s.add(c);
    });
    for (const it of items || []) {
      if (!it || it.targetSection !== "medicines") continue;
      if ((it.operation || "add") !== "add") continue;
      const isCommitted = it.state === "added_in_call" || it.state === "edited_in_call";
      const isNewReject = it.state === "rejected" && !it.dbRecordRef && it.lastCommitted;
      if (!isCommitted && !isNewReject) continue;
      const payload = it.lastCommitted || it.current || {};
      const c = payload.mcode;
      if (c) s.add(c);
    }
    return s;
  }, [medDoc, items]);

  // Drug schedule — staged plans (added or edited during this call)
  // that DrugScheduleEditor's picker should also treat as "already on
  // an active schedule" so the reviewer can't add the same drug twice
  // via two staged rows. Server-loaded active plans are already
  // handled inside DrugScheduleEditor via its own list fetch.
  const stagedDrugSchedNames = useMemo(() => {
    const s = new Set();
    for (const it of items || []) {
      if (!it || it.targetSection !== "drug_schedule") continue;
      if ((it.operation || "add") !== "add") continue;
      // Include three states so the picker blocks a same-drug re-add
      // via EITHER a committed-in-call plan OR a rejected plan that's
      // still showing on the table (strikethrough row). Previously
      // only committed states counted; after rejecting a plan the
      // reviewer could pick the same drug again from the dropdown
      // and spawn a second captured — but the strikethrough row kept
      // the slot visually occupied, so the double-pick read as "wait,
      // isn't it already there?".
      const include = it.state === "added_in_call"
        || it.state === "edited_in_call"
        || (it.state === "rejected" && !it.dbRecordRef && it.lastCommitted && !it.deletedAt);
      if (!include) continue;
      // For a rejected item the server cleared Current but preserved
      // LastCommitted — read whichever side carries the drug name.
      const p = it.lastCommitted || it.current || {};
      const firstDose = Array.isArray(p.doses) && p.doses[0];
      const name = String(
        p.drug_name || (firstDose && firstDose.dosename) || "",
      ).trim().toLowerCase();
      if (name) s.add(name);
    }
    return s;
  }, [items]);

  const heading = SECTION_HEADING[activeSection] || "Section";

  // Right column is empty when the call has ended, consent isn't
  // being awaited, and the active section has no AI proposals and no
  // open manual / update / re-edit cards. In that state we collapse
  // the 340-px right slot and let the middle table use the full
  // width — otherwise the split reads as a big empty column past
  // Save & next.
  const hasRightSideCardsForActiveSection = (() => {
    switch (activeSection) {
      case "chief_complaints":
        return manualCardIds.length + updateCards.length + reeditCards.length > 0;
      case "diagnosis":
        return manualCardIdsDx.length + updateCardsDx.length + reeditCardsDx.length > 0;
      case "medicines":
        return manualCardIdsMed.length + reeditCardsMed.length + updateCardsMed.length > 0;
      case "hospitalisation":
        return manualCardIdsHosp.length + reeditCardsHosp.length + updateCardsHosp.length > 0;
      case "lab_investigations":
        return manualCardIdsLab.length + reeditCardsLab.length > 0;
      case "allergies":
        return manualCardIdsAlg.length + reeditCardsAlg.length + updateCardsAlg.length > 0;
      case "family_history":
        return manualCardIdsFh.length + reeditCardsFh.length + updateCardsFh.length > 0;
      case "addictions":
        return updateCardsAdd.length + reeditCardsAdd.length > 0;
      case "activity":
        return manualCardIdsAct.length + reeditCardsAct.length + updateCardsAct.length > 0;
      case "anthropometric":
        return manualCardsAnth.length + reeditCardsAnth.length > 0;
      case "daily_schedule":
        return reeditCardsDs.length > 0;
      case "food_preferences":
        // No right-rail card for food preferences (AI captures merge
        // into the middle-column form directly via FP_V2_AI_INJECT).
        // Return false so `rightColumnEmpty` collapses the 340-px slot.
        return false;
      case "drug_schedule":
        // The DrugScheduleEditor is only visible when a sheet is
        // open (add / update). While idle, collapse the rail so the
        // middle-column table spans full width like CC / Dx.
        return dsSheetOpen;
      case "patient_details":
        // Patient details rail only surfaces when a row click opens it.
        // Same idle-collapsed pattern as drug_schedule — otherwise
        // callEnded + no proposals + no open card would leave a 340px
        // empty column even when the reviewer isn't editing.
        return patientDetailsOpen;
      default:
        return false;
    }
  })();
  const rightColumnEmpty =
    // Past-transcript view: right column stays collapsed UNTIL the
    // reviewer clicks a row → then the S5-style card opens in it.
    (viewMode === "review" && !openedReviewId) ||
    (viewMode !== "review" && !!activeSection &&
      !awaitingConsent &&
      callEnded &&
      proposals.length === 0 &&
      !hasRightSideCardsForActiveSection);

  // ZY-10356 Phase 5 — lockout state for the currently-active section.
  // When the server has stamped reviewedSections[activeSection], Call
  // mode must render a banner + visually disable the content area.
  const activeReviewMeta = (activeSection && reviewedSections)
    ? reviewedSections[activeSection]
    : null;
  const isActiveSectionReviewed = !!activeReviewMeta;
  const reviewerName = (activeReviewMeta && (
    activeReviewMeta.reviewedByName
    || (activeReviewMeta.reviewedBy && String(activeReviewMeta.reviewedBy.whoId || "").trim())
  )) || "";
  const reviewedAt = (activeReviewMeta && activeReviewMeta.reviewedAt) || "";
  const reviewedAtLabel = reviewedAt
    ? new Date(reviewedAt).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : "";

  return (
    <div className="aic-shell">
      {!hideTabs && (
        <SectionTabs
          tabs={tabs}
          activeTab={activeTab}
          onSelect={onSelectTab}
          openCounts={openCountsBySection}
          reviewedSections={reviewedSections}
        />
      )}
      {isActiveSectionReviewed ? (
        <div className="aic-lockout" role="status" aria-live="polite">
          <div className="aic-lockout-head">
            <span className="aic-lockout-check" aria-hidden="true">&#10003;</span>
            <span className="aic-lockout-title">
              {reviewerName || reviewedAtLabel ? (
                <>
                  REVIEWED
                  {reviewerName ? <> BY <strong>{reviewerName}</strong></> : null}
                  {reviewedAtLabel ? <> · {reviewedAtLabel}</> : null}
                </>
              ) : (
                <>Section already reviewed</>
              )}
            </span>
          </div>
          <p className="aic-lockout-body">
            This section is already reviewed and cannot be edited now —
            any further change has to be made in Orbit.
          </p>
        </div>
      ) : null}

      <div className={`aic-split${rightColumnEmpty ? " is-right-empty" : ""}${isActiveSectionReviewed ? " is-locked" : ""}`}>
        {/* Left half — the active section's own standalone renderer.
            Chief Complaints reuses the sidebar component as-is (must
            look identical inside vs outside the modal). Other sections
            still fall back to the generic table for now until they're
            individually integrated. */}
        <div className="aic-table-area">
          {activeSection == null ? (
            <div className="aic-empty">
              <Sparkle className="aic-empty-spark" />
              <p className="aic-empty-title">Select a section from the tabs above</p>
              <p className="aic-empty-sub">
                The section's existing view appears here; AI-proposed items
                appear on the right.
              </p>
            </div>
          ) : viewMode === "review" ? (
            /* Past-transcript view (reopened, non-live) — show ONLY the
               items THIS transcript captured, in the same per-section
               table layout the S5 Review screen uses. No standalone
               section embed here (the reviewer already saw the full
               patient list before opening; the transcript view is a
               focused summary of what THIS call added / modified). */
            forSection.length === 0 ? (
              <div className="aic-empty">
                <p className="aic-empty-title">Nothing captured</p>
                <p className="aic-empty-sub">
                  This transcript didn&rsquo;t add or modify any {heading.toLowerCase()} entries.
                </p>
              </div>
            ) : (
              <SectionCaptureTable
                section={activeSection}
                items={forSection}
                editingId={openedReviewId}
                patientId={patientId}
                onEdit={(it) => setOpenedReviewId(it.id)}
              />
            )
          ) : activeSection === "chief_complaints" ? (
            <ChiefComplaints patient={patient} capturedItems={forSection} onRowsSync={setCcRows} />
          ) : activeSection === "diagnosis" ? (
            <Diagnosis patient={patient} capturedItems={forSection} onRowsSync={setDxRows} />
          ) : activeSection === "medicines" ? (
            <Medicines patient={patient} capturedItems={forSection} onRowsSync={setMedRows} />
          ) : activeSection === "hospitalisation" ? (
            <Hospitalisation patient={patient} capturedItems={forSection} onRowsSync={setHospRows} />
          ) : activeSection === "lab_investigations" ? (
            <LabInvestigations patient={patient} capturedItems={forSection} />
          ) : activeSection === "allergies" ? (
            <Allergies patient={patient} capturedItems={forSection} onRowsSync={setAlgRows} />
          ) : activeSection === "family_history" ? (
            <FamilyHistory patient={patient} capturedItems={forSection} onRowsSync={setFhRows} />
          ) : activeSection === "addictions" ? (
            <Addictions patient={patient} capturedItems={forSection} onRowsSync={setAddRows} />
          ) : activeSection === "activity" ? (
            <PhysicalActivity patient={patient} capturedItems={forSection} onRowsSync={setActRows} />
          ) : activeSection === "anthropometric" ? (
            <Anthropometric patient={patient} capturedItems={forSection} onRowsSync={setAnthRows} />
          ) : activeSection === "daily_schedule" ? (
            /* Rendered as a keep-mounted sibling below so switching
               tabs doesn't drop the reviewer's typed builder state.
               Returning null here lets the sibling own the slot. */
            null
          ) : activeSection === "food_preferences" ? (
            /* Same keep-mounted treatment as daily_schedule — the whole
               FP form is a single Dirty-tracking component, unmounting
               loses chip picks + autocompletes. */
            null
          ) : activeSection === "patient_details" ? (
            /* Patient details in transcript mode — Wave 2. Renders the
               standalone table with staged captures overlaid: fields
               with a pending update show peach + (for AI captures) a
               sparkle. Reviewer clicks any row → right column opens
               the 5-field form seeded from the staged overlay; saving
               UPSERTS onto the single "patient_details:main" captured
               item. Commit happens later at Review Save & next.
               `onEdit` bypasses the PD_V2_EDIT event bus so the
               standalone shell's dormant PatientDetailsEditor listener
               doesn't fight for the same open state. */
            <PatientDetails
              patient={patient}
              capturedItems={forSection}
              onEdit={openPatientDetailsRail}
            />
          ) : activeSection === "drug_schedule" ? (
            /* Keep-mounted sibling below. See daily_schedule comment. */
            null
          ) : (
            <div className="aic-tblcard">
              <header className="aic-tblcard-head">
                <h3 className="aic-tblcard-title">{heading}</h3>
                <span className="aic-tblcard-count">
                  {addedRows.length}
                  {addedRows.length === 1 ? " entry" : " entries"}
                </span>
              </header>
              <p className="aic-tblcard-empty">
                Section not integrated yet. This tab currently only shows the
                AI proposals on the right.
              </p>
            </div>
          )}

          {/* ZY-10356 Phase 1 — keep-mounted siblings for sections whose
              whole-component holds internal form state. Once visited,
              each stays mounted and CSS-swaps visibility on tab change
              so the reviewer's in-progress edits (DS builder, DailySchedule
              timeline draft, Food preferences chip picks) survive. */}
          {hasVisited("daily_schedule") && viewMode !== "review" && (
            <div style={{ display: activeSection === "daily_schedule" ? undefined : "none" }}>
              <DailySchedule
                patient={patient}
                capturedItems={forSection}
                draftKey={transcriptId ? `daily-schedule:${transcriptId}` : "daily-schedule"}
              />
            </div>
          )}
          {hasVisited("food_preferences") && viewMode !== "review" && (
            <div style={{ display: activeSection === "food_preferences" ? undefined : "none" }}>
              <FoodPreference
                patient={patient}
                /* Scope the draft key to this transcript so FP edits on
                   one transcript don't leak into another's view. */
                draftKey={transcriptId ? `food-preferences:${transcriptId}` : "food-preferences"}
              />
            </div>
          )}
          {hasVisited("drug_schedule") && viewMode !== "review" && (
            <div style={{ display: activeSection === "drug_schedule" ? undefined : "none" }}>
              <DrugSchedule patient={patient} capturedItems={forSection} transcriptMode />
            </div>
          )}
        </div>

        {/* Right half — Read-only proposal preview cards. No actions
            during the call; every decision is deferred to the review
            flow (post-call). Empty-state copy branches on whether
            consent has been captured — a silent right column is almost
            always because /captured is being 409-rejected by the server
            consent gate. */}
        <div className="aic-cards-area">
          {viewMode === "review" ? (
            openedReviewId ? (
              (() => {
                const captured = forSection.find((it) => it.id === openedReviewId);
                if (!captured) return null;
                return (
                  <CapturedItemCard
                    captured={captured}
                    section={activeSection}
                    patientId={patient && patient.id}
                    saving={false}
                    error=""
                    onSave={() => setOpenedReviewId("")}
                    onClose={() => setOpenedReviewId("")}
                    readOnly
                  />
                );
              })()
            ) : null
          ) : activeSection == null ? null : activeSection === "chief_complaints" ? (
            <>
              {/* Update cards from status-chip clicks surface at the very
                  top so the care manager doesn't miss the one they just
                  triggered. */}
              {updateCards.map(({ cardId, complaint }) => (
                <ComplaintForm
                  key={cardId}
                  formId={cardId}
                  patientId={patientId}
                  editing={complaint}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveUpdateCard(
                    cardId,
                    complaint,
                    ccBodyToCapturedUpdate(body, {
                      complaint: complaint.complaint,
                      complaintCode: complaint.complaintCode,
                    }),
                  )}
                  onClose={() => cancelUpdateCard(cardId)}
                  extraActiveKeys={stagedCcIcdCodes}
                  themeClass="is-update"
                  menuClassName="ait-menu"
                />
              ))}
              {/* Re-edit cards for already-staged captured items. Origin
                  drives the mode: staged-add → ai/manual (whichever the
                  original was), staged-update → update. Save updates the
                  same captured item in place. */}
              {reeditCards.map(({ cardId, captured, ccv2Record, initialPayloadOverride }) => {
                if (captured.operation === "update" && ccv2Record) {
                  // Synthesise `editing` from the underlying CC v2 record
                  // + the staged payload override so the update form
                  // pre-fills with the last-saved staged values.
                  // ZY-10387 Shift 2: read captured.effective (server-
                  // computed {...lastCommitted, ...current}, nulls
                  // already stripped by Shift 1) so the Update form
                  // pre-fills with the CM's committed edits overlaid
                  // with the agent's latest fields — no client-side
                  // merge needed. Falls back for a backend pre-dating
                  // Shift 2.
                  const p = captured.effective || initialPayloadOverride || captured.current || {};
                  const editing = {
                    ...ccv2Record,
                    current: {
                      vas:              typeof p.vas_min === "number" ? p.vas_min : (ccv2Record.current && ccv2Record.current.vas),
                      sinceWhen:        p.since_when || (ccv2Record.current && ccv2Record.current.sinceWhen),
                      description:      p.description || (ccv2Record.current && ccv2Record.current.description) || "",
                      consultedDoctor:  !!p.consulted_doctor,
                    },
                  };
                  // AI-origin update re-edit gets the brown AI theme; a
                  // manual-origin staged update stays on the update slate
                  // theme (indigo button + "Updating" chip). The single
                  // rule is: AI-populated → brown, everything else →
                  // indigo (with is-update reserved for the update chip).
                  const wasAiUpdate = captured.source === "ai";
                  return (
                    <ComplaintForm
                      key={cardId}
                      formId={cardId}
                      patientId={patientId}
                      editing={editing}
                      saving={!!saving[cardId]}
                      error={errors[cardId] || ""}
                      onSave={(body) => saveReeditCard(
                        cardId,
                        captured,
                        ccBodyToCapturedUpdate(body, {
                          complaint: ccv2Record.complaint,
                          complaintCode: ccv2Record.complaintCode,
                        }),
                      )}
                      onReject={() => rejectReeditCard(cardId, captured)}
                      onClose={() => cancelReeditCard(cardId, captured)}
                      extraActiveKeys={stagedCcIcdCodes}
                      themeClass={wasAiUpdate ? "is-ai" : "is-update"}
                      menuClassName="ait-menu"
                      isReopen
                      isAiSourced={wasAiUpdate}
                    />
                  );
                }
                // Staged-add re-edit — same shape as the original AI/
                // manual card, but Save updates the existing captured
                // item via decide (payload override).
                const wasAi = captured.source === "ai";
                return (
                  <ComplaintForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    initialPayload={initialPayloadOverride || captured.current}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCard(cardId, captured, ccBodyToCapturedAdd(body))}
                    onReject={() => rejectReeditCard(cardId, captured)}
                    onClose={() => cancelReeditCard(cardId, captured)}
                    extraActiveKeys={stagedCcIcdCodes}
                    themeClass={wasAi ? "is-ai" : "is-manual"}
                    menuClassName="ait-menu"
                    isReopen
                    isAiSourced={wasAi}
                  />
                );
              })}
              {/* Manual (blank) cards next, above AI cards. */}
              {manualCardIds.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <ComplaintForm
                    formId={cardId}
                    patientId={patientId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onSave={(body) => saveManualCard(cardId, ccBodyToCapturedAdd(body))}
                    onReject={(fid, body) => rejectManualCard(fid, ccBodyToCapturedAdd(body))}
                    onClose={() => cancelManualCard(cardId)}
                    extraActiveKeys={stagedCcIcdCodes}
                    themeClass="is-manual"
                    menuClassName="ait-menu"
                  />
                </div>
              ))}
              {proposals.map((it) => {
                // AI-match-to-DB dedup: if the proposal's complaint name
                // or ICD code exactly matches an existing CC v2 row,
                // open it as an update card on that row (#7 shape)
                // instead of a duplicate add. Mirrors Allergies / FH /
                // Activity. Match by ICD code first (unambiguous), fall
                // back to case-insensitive complaint name.
                const propIcd = ((it.current && it.current.icd_code) || "").trim();
                const propName = ((it.current && (it.current.complaint || it.current.value)) || "").trim().toLowerCase();
                let match = null;
                if (propIcd && Array.isArray(ccRows)) {
                  match = ccRows.find((r) => r && r.complaintCode && String(r.complaintCode.icdCode || "").trim() === propIcd);
                }
                if (!match && propName && Array.isArray(ccRows)) {
                  match = ccRows.find((r) => r && String(r.complaint || "").trim().toLowerCase() === propName);
                }
                if (match) {
                  // Render as a DB-update card (editing= + lockName).
                  // Save routes through saveAiProposalAsUpdate so the
                  // proposal is re-materialised server-side as
                  // operation=update with dbRecordRef=complaintId.
                  return (
                    <ComplaintForm
                      key={it.id}
                      formId={it.id}
                      patientId={patientId}
                      editing={match}
                      initialPayload={it.current}
                      saving={!!saving[it.id]}
                      error={errors[it.id] || ""}
                      onSave={(body) => saveAiProposalAsUpdate(
                        it, "chief_complaints", match.complaintId,
                        ccBodyToCapturedUpdate(body, {
                          complaint: match.complaint,
                          complaintCode: match.complaintCode,
                        }),
                      )}
                      onClose={() => discardAi(it)}
                      extraActiveKeys={stagedCcIcdCodes}
                      themeClass="is-ai"
                      menuClassName="ait-menu"
                      lockName
                      isReopen={!!it.lastCommitted}
                      isAiSourced
                    />
                  );
                }
                return (
                  <ComplaintForm
                    key={it.id}
                    formId={it.id}
                    patientId={patientId}
                    initialPayload={it.current}
                    saving={!!saving[it.id]}
                    error={errors[it.id] || ""}
                    onSave={(body) => saveAiCard(it, ccBodyToCapturedAdd(body))}
                    onClose={() => discardAi(it)}
                    extraActiveKeys={stagedCcIcdCodes}
                    themeClass="is-ai"
                    menuClassName="ait-menu"
                    /* AI-emitted first-open (#3) has no LastCommitted; a
                       revived rejected card (#4 flow after AI re-emits
                       onto a rejected item, server flips state → pending)
                       DOES carry LastCommitted and reads as a re-open. */
                    isReopen={!!it.lastCommitted}
                    isAiSourced
                  />
                );
              })}
              {manualCardIds.length === 0 && proposals.length === 0 && updateCards.length === 0 && reeditCards.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add complaints</strong> on the left to add a
                      manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "diagnosis" ? (
            <>
              {updateCardsDx.map(({ cardId, diagnosis: d }) => (
                <DiagnosisForm
                  key={cardId}
                  formId={cardId}
                  patientId={patientId}
                  editing={d}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveUpdateCardDx(
                    cardId,
                    d,
                    dxBodyToCapturedUpdate(body, {
                      diagnosis:     d.diagnosis,
                      diagnosisCode: d.diagnosisCode,
                    }),
                  )}
                  onClose={() => cancelUpdateCardDx(cardId)}
                  extraActiveKeys={stagedDxIcdCodes}
                  themeClass="is-update"
                  menuClassName="ait-menu"
                />
              ))}
              {reeditCardsDx.map(({ cardId, captured, dxv2Record, initialPayloadOverride }) => {
                // If dxv2Record wasn't attached at creation time
                // (openReedit sites that pre-date the dxRows lookup, or
                // Shift-4 auto-reopen without a matching row at the time
                // the card was synthesised), look it up now from the
                // live dxRows via dbRecordRef so the update branch below
                // can fire. Mirrors the CC fallback in ccv2Record branch.
                let effectiveRecord = dxv2Record;
                if (!effectiveRecord && captured.operation === "update" && captured.dbRecordRef) {
                  effectiveRecord = (dxRows || []).find((r) => String(r.diagnosisId) === String(captured.dbRecordRef)) || null;
                }
                if (captured.operation === "update" && effectiveRecord) {
                  // ZY-10387 Shift 2: prefer `effective` (null-stripped
                  // overlay of LastCommitted + Current) over Current so a
                  // reviewer edit held in LastCommitted isn't clobbered
                  // by a later AI-only emission.
                  const p = captured.effective || captured.current || {};
                  const editing = {
                    ...effectiveRecord,
                    ongoing: p.treatment_status
                      ? String(p.treatment_status).toLowerCase() === "ongoing"
                      : !!effectiveRecord.ongoing,
                    current: {
                      sinceWhen:       p.since_when || (effectiveRecord.current && effectiveRecord.current.sinceWhen),
                      description:     p.description || (effectiveRecord.current && effectiveRecord.current.description) || "",
                      consultedDoctor: !!p.consulted_doctor,
                    },
                  };
                  const wasAiUpdate = captured.source === "ai";
                  return (
                    <DiagnosisForm
                      key={cardId}
                      formId={cardId}
                      patientId={patientId}
                      editing={editing}
                      saving={!!saving[cardId]}
                      error={errors[cardId] || ""}
                      onSave={(body) => saveReeditCardDx(
                        cardId,
                        captured,
                        dxBodyToCapturedUpdate(body, {
                          diagnosis:     effectiveRecord.diagnosis,
                          diagnosisCode: effectiveRecord.diagnosisCode,
                        }),
                      )}
                      onReject={() => rejectReeditCardDx(cardId, captured)}
                      onClose={() => cancelReeditCardDx(cardId, captured)}
                      extraActiveKeys={stagedDxIcdCodes}
                      themeClass={wasAiUpdate ? "is-ai" : "is-update"}
                      menuClassName="ait-menu"
                      isReopen
                    />
                  );
                }
                const wasAi = captured.source === "ai";
                return (
                  <DiagnosisForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    initialPayload={initialPayloadOverride || captured.current}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardDx(cardId, captured, dxBodyToCapturedAdd(body))}
                    onReject={() => rejectReeditCardDx(cardId, captured)}
                    onClose={() => cancelReeditCardDx(cardId, captured)}
                    extraActiveKeys={stagedDxIcdCodes}
                    themeClass={wasAi ? "is-ai" : "is-manual"}
                    menuClassName="ait-menu"
                    /* ZY-10356 Phase 6 Rule 1 — new-entry re-open (#2/#4)
                       looks the same as first-open (#1/#3) with data
                       filled; the X top-right (gated on isReopen) is the
                       only differentiator. Main field (Diagnosis + ICD)
                       stays editable so the reviewer can repick without
                       closing + re-adding. */
                    isReopen
                  />
                );
              })}
              {manualCardIdsDx.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <DiagnosisForm
                    formId={cardId}
                    patientId={patientId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onSave={(body) => saveManualCardDx(cardId, dxBodyToCapturedAdd(body))}
                    onClose={() => cancelManualCardDx(cardId)}
                    extraActiveKeys={stagedDxIcdCodes}
                    themeClass="is-manual"
                    menuClassName="ait-menu"
                  />
                </div>
              ))}
              {proposals.map((it) => {
                // AI-match-to-DB dedup (ZY-10387 safety net): if the
                // proposal's ICD or diagnosis name exactly matches an
                // existing Dx v2 row, open it as an update card on that
                // row (#7 shape) instead of a duplicate add. Mirrors CC
                // proposals.map. Match ICD first (unambiguous), then
                // case-insensitive diagnosis name. Backend Shift 3 does
                // the same promotion server-side when the ICD matches
                // an ONGOING row; this block covers the resolved-row
                // fallback and dev envs where DX_V2_URL is unset.
                const propIcd = ((it.current && it.current.icd_code) || "").trim();
                const propName = ((it.current && (it.current.name || it.current.diagnosis || it.current.value)) || "").trim().toLowerCase();
                let match = null;
                if (propIcd && Array.isArray(dxRows)) {
                  match = dxRows.find((r) => r && r.diagnosisCode && String(r.diagnosisCode.icdCode || "").trim() === propIcd);
                }
                if (!match && propName && Array.isArray(dxRows)) {
                  match = dxRows.find((r) => r && String(r.diagnosis || "").trim().toLowerCase() === propName);
                }
                if (match) {
                  return (
                    <DiagnosisForm
                      key={it.id}
                      formId={it.id}
                      patientId={patientId}
                      editing={match}
                      initialPayload={it.current}
                      saving={!!saving[it.id]}
                      error={errors[it.id] || ""}
                      onSave={(body) => saveAiProposalAsUpdate(
                        it, "diagnosis", match.diagnosisId,
                        dxBodyToCapturedUpdate(body, {
                          diagnosis:     match.diagnosis,
                          diagnosisCode: match.diagnosisCode,
                        }),
                      )}
                      onClose={() => discardAi(it)}
                      extraActiveKeys={stagedDxIcdCodes}
                      themeClass="is-ai"
                      menuClassName="ait-menu"
                      lockName
                      isReopen={!!it.lastCommitted}
                    />
                  );
                }
                return (
                  <DiagnosisForm
                    key={it.id}
                    formId={it.id}
                    patientId={patientId}
                    initialPayload={it.current}
                    saving={!!saving[it.id]}
                    error={errors[it.id] || ""}
                    onSave={(body) => saveAiCardDx(it, dxBodyToCapturedAdd(body))}
                    onClose={() => discardAi(it)}
                    extraActiveKeys={stagedDxIcdCodes}
                    themeClass="is-ai"
                    menuClassName="ait-menu"
                    /* AI-emitted first-open has no LastCommitted; a
                       revived rejected card (ZY-10387 revive branch for
                       Dx) DOES carry LastCommitted and reads as a
                       re-open — show the X close. */
                    isReopen={!!it.lastCommitted}
                  />
                );
              })}
              {manualCardIdsDx.length === 0 && proposals.length === 0 && updateCardsDx.length === 0 && reeditCardsDx.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add diagnosis</strong> on the left to add a
                      manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "medicines" ? (
            <>
              {/* Update cards for existing Med v2 rows the reviewer
                  clicked in the middle-column table (medicine name /
                  STATUS chip → MED_V2_STATUS event). MedicineStatusForm
                  keeps its internal service call for save on this Wave-
                  A cut — status transitions on an already-persisted
                  medicine don't need transcript-side staging. */}
              {updateCardsMed.map(({ cardId, medicine }) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <MedicineStatusForm
                    formId={cardId}
                    patientId={patientId}
                    editing={medicine}
                    // MedicineStatusForm requires the full sheet-stack
                    // callback set. In transcript mode we don't share a
                    // save-lock across the rail (only one status card
                    // open per medicine, and the save hits Medicine v2
                    // directly), so onSavingChange is a no-op here.
                    // onFilledChange still routes into the shared blank
                    // tracker so the guard treats an unmodified update
                    // card the same way it treats an add card.
                    onFilledChange={onCardFilledChange}
                    onSavingChange={() => {}}
                    onClose={() => setUpdateCardsMed((prev) => prev.filter((u) => u.cardId !== cardId))}
                    // Lift the Status Select's portaled menu above the
                    // AiTranscript modal overlay — see aiTranscript.scss
                    // `body > .zy-select-menu.ait-menu`. Without this
                    // the dropdown opens behind the modal and option
                    // clicks land on the backdrop (reported as "unable
                    // to click Status dropdown").
                    menuClassName="ait-menu"
                  />
                </div>
              ))}
              {reeditCardsMed.map(({ cardId, captured, initialPayloadOverride }) => {
                const wasAiMed = captured.source === "ai";
                return (
                  <MedicineForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    /* ZY-10387 Shift 2: prefer `effective` (null-stripped
                       overlay of LastCommitted + Current) so a reviewer
                       edit held in LastCommitted isn't clobbered by a
                       later AI-only emission. Fall back for pre-Shift-2
                       server builds. */
                    initialPayload={initialPayloadOverride || captured.effective || captured.current}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardMed(cardId, captured, medBodyToCapturedAdd(body))}
                    onReject={() => rejectReeditCardMed(cardId, captured)}
                    onClose={() => cancelReeditCardMed(cardId, captured)}
                    themeClass={wasAiMed ? "is-ai" : "is-manual"}
                    /* ZY-10356 Phase 6 Rule 1 — see DiagnosisForm note
                       above. Main field (medicine name picker) stays
                       editable; X top-right from isReopen is the only
                       re-open differentiator. */
                    menuClassName="ait-menu"
                    isReopen
                  />
                );
              })}
              {manualCardIdsMed.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <MedicineForm
                    formId={cardId}
                    patientId={patientId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onSave={(body) => saveManualCardMed(cardId, medBodyToCapturedAdd(body))}
                    onClose={() => cancelManualCardMed(cardId)}
                    themeClass="is-manual"
                    blockedMcodes={medBlockedMcodes}
                    menuClassName="ait-menu"
                  />
                </div>
              ))}
              {proposals.map((it) => (
                // ZY-10387 phase scope: medicine UPDATE flows (#5-#8)
                // are deliberately disabled — AI proposals that would
                // otherwise match an existing Med v2 row render as a
                // plain AI add instead. The reviewer either Adds (and
                // Med v2 may 409 "already-active", surfacing as a
                // LastError badge on the row) or Rejects. The dormant
                // AI-match-to-DB block that routed matches through
                // saveAiProposalAsUpdate("medicines", …) was removed
                // in this phase; see git history to restore when the
                // update flow is unblocked.
                <MedicineForm
                  key={it.id}
                  formId={it.id}
                  patientId={patientId}
                  initialPayload={it.current}
                  saving={!!saving[it.id]}
                  error={errors[it.id] || ""}
                  onSave={(body) => saveAiCardMed(it, medBodyToCapturedAdd(body))}
                  onClose={() => discardAi(it)}
                  themeClass="is-ai"
                  blockedMcodes={medBlockedMcodes}
                  menuClassName="ait-menu"
                  /* AI-emitted first-open (#3) has no LastCommitted; a
                     revived rejected card (#4 flow after AI re-emits
                     onto a rejected item, server flips state → pending)
                     DOES carry LastCommitted and reads as a re-open. */
                  isReopen={!!it.lastCommitted}
                />
              ))}
              {manualCardIdsMed.length === 0 && proposals.length === 0 && reeditCardsMed.length === 0 && updateCardsMed.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add medicine</strong> on the left to add a
                      manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "hospitalisation" ? (
            <>
              {/* Update cards for existing Hosp v2 records that the
                  reviewer clicked in the middle-column table. Same shape
                  as the CC update card — form opens in edit mode with the
                  record pre-filled; Save propose+decide-flows a staged
                  update on the transcript's captured buffer. */}
              {updateCardsHosp.map(({ cardId, record }) => (
                <HospitalisationForm
                  key={cardId}
                  formId={cardId}
                  patientId={patientId}
                  editing={record}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveUpdateCardHosp(cardId, record, hospBodyToCapturedUpdate(body, { record }))}
                  onClose={() => cancelUpdateCardHosp(cardId)}
                  themeClass="is-update"
                  menuClassName="ait-menu"
                />
              ))}
              {/* Wave A first-cut for hosp: same shape as meds — manual +
                  AI-proposal + staged-add re-edit. */}
              {reeditCardsHosp.map(({ cardId, captured, hospv2Record, initialPayloadOverride }) => {
                const wasAiHosp = captured.source === "ai";
                // ZY-10387 Shift 2: prefer `effective` (null-stripped
                // overlay of LastCommitted + Current) so a reviewer
                // edit held in LastCommitted isn't clobbered by a
                // later AI-only emission. Fall back for pre-Shift-2
                // server builds.
                const initP = initialPayloadOverride || captured.effective || captured.current;
                // Lazy-resolve hospv2Record from hospRows if the
                // attached one is null — covers Shift-4 auto-reopens
                // where the record wasn't on hand at card creation
                // time. Mirrors the CC / Dx fallback.
                let effectiveRecord = hospv2Record;
                if (!effectiveRecord && captured.operation === "update" && captured.dbRecordRef) {
                  effectiveRecord = (hospRows || []).find((r) => String(r.hospitalisationId) === String(captured.dbRecordRef)) || null;
                }
                if (captured.operation === "update" && effectiveRecord) {
                  return (
                    <HospitalisationForm
                      key={cardId}
                      formId={cardId}
                      patientId={patientId}
                      editing={effectiveRecord}
                      initialPayload={initP}
                      saving={!!saving[cardId]}
                      error={errors[cardId] || ""}
                      onSave={(body) => saveReeditCardHosp(cardId, captured, hospBodyToCapturedUpdate(body, { record: effectiveRecord }))}
                      onReject={() => rejectReeditCardHosp(cardId, captured)}
                      onClose={() => cancelReeditCardHosp(cardId, captured)}
                      themeClass={wasAiHosp ? "is-ai" : "is-update"}
                      lockName
                      /* #6/#8 — DB-update re-open. Keep lockName (identity
                         of the DB row shouldn't change on re-edit) + add
                         isReopen so the X top-right surfaces. */
                      isReopen
                    />
                  );
                }
                return (
                  <HospitalisationForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    initialPayload={initP}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardHosp(cardId, captured, hospBodyToCapturedAdd(body))}
                    onReject={() => rejectReeditCardHosp(cardId, captured)}
                    onClose={() => cancelReeditCardHosp(cardId, captured)}
                    themeClass={wasAiHosp ? "is-ai" : "is-manual"}
                    /* ZY-10356 Phase 6 Rule 1 — new-entry re-open (#2/#4).
                       Main field (cause of hospitalisation) editable; X
                       top-right from isReopen is the only differentiator. */
                    isReopen
                  />
                );
              })}
              {manualCardIdsHosp.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <HospitalisationForm
                    formId={cardId}
                    patientId={patientId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onSave={(body) => saveManualCardHosp(cardId, hospBodyToCapturedAdd(body))}
                    onClose={() => cancelManualCardHosp(cardId)}
                    themeClass="is-manual"
                  />
                </div>
              ))}
              {proposals.map((it) => {
                // AI-match-to-DB dedup (ZY-10387 safety net for Hosp):
                // match an AI proposal against an existing Hosp v2
                // episode and open it as an update card instead of a
                // duplicate add. Match semantics differ from CC/Dx
                // because Hosp uses a COMPOSITE key — same ICD on
                // different admit dates = different episodes. Match
                // priority:
                //   1. ICD + admit date (unambiguous when AI emits
                //      date_of_surgery).
                //   2. ICD alone — only if there's EXACTLY ONE row
                //      with that ICD (avoids ambiguity when the AI
                //      doesn't know the date and the patient has
                //      multiple admits for the same condition).
                //   3. case-insensitive cause name — same single-row
                //      guard.
                // Backend Shift 3 covers ongoing-admit auto-promote
                // server-side when HOSP_V2_URL is configured; this
                // block is the belt-and-suspenders for discharged
                // episodes and dev envs.
                const propIcd = ((it.current && it.current.icd_code) || "").trim();
                const propDate = ((it.current && (it.current.date_of_surgery || it.current.since_date)) || "").trim();
                const propName = ((it.current && (it.current.name || it.current.value)) || "").trim().toLowerCase();
                let match = null;
                if (propIcd && propDate && Array.isArray(hospRows)) {
                  match = hospRows.find((r) => {
                    const icd = String((r.cause && r.cause.icdCode) || "").trim();
                    const date = String(r.hospitalisationDate || "").slice(0, 10);
                    return icd === propIcd && date === propDate.slice(0, 10);
                  });
                }
                if (!match && propIcd && Array.isArray(hospRows)) {
                  const sameIcd = hospRows.filter((r) => r && r.cause && String(r.cause.icdCode || "").trim() === propIcd);
                  if (sameIcd.length === 1) match = sameIcd[0];
                }
                if (!match && propName && Array.isArray(hospRows)) {
                  const sameName = hospRows.filter((r) => {
                    const label = String((r.cause && (r.cause.icdName || r.cause.description)) || "").trim().toLowerCase();
                    return label === propName;
                  });
                  if (sameName.length === 1) match = sameName[0];
                }
                if (match) {
                  return (
                    <HospitalisationForm
                      key={it.id}
                      formId={it.id}
                      patientId={patientId}
                      editing={match}
                      initialPayload={it.current}
                      saving={!!saving[it.id]}
                      error={errors[it.id] || ""}
                      onSave={(body) => saveAiProposalAsUpdate(
                        it, "hospitalisation", match.hospitalisationId,
                        hospBodyToCapturedUpdate(body, { record: match }),
                      )}
                      onClose={() => discardAi(it)}
                      themeClass="is-ai"
                      lockName
                      isReopen={!!it.lastCommitted}
                    />
                  );
                }
                return (
                  <HospitalisationForm
                    key={it.id}
                    formId={it.id}
                    patientId={patientId}
                    initialPayload={it.current}
                    saving={!!saving[it.id]}
                    error={errors[it.id] || ""}
                    onSave={(body) => saveAiCardHosp(it, hospBodyToCapturedAdd(body))}
                    onClose={() => discardAi(it)}
                    themeClass="is-ai"
                    /* Revived rejected Hosp proposals (ZY-10387 revive
                       branch) carry LastCommitted and read as re-open —
                       show the X close. */
                    isReopen={!!it.lastCommitted}
                  />
                );
              })}
              {manualCardIdsHosp.length === 0 && proposals.length === 0 && reeditCardsHosp.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add hospitalisation</strong> on the left to
                      add a manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "lab_investigations" ? (
            <>
              {/* Wave A first-cut for lab investigations: same shape as
                  meds / hosp — manual + AI-proposal + staged-add re-edit.
                  Update auto-route deferred. */}
              {reeditCardsLab.map(({ cardId, captured }) => {
                const wasAiLab = captured.source === "ai";
                const editing = labCapturedItemsToCardEditing([captured]);
                return (
                  <DigitiseManuallyCard
                    key={cardId}
                    formId={cardId}
                    editing={editing}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onAdd={(commit) => {
                      const payload = labCommitToPrimaryCapturedAdd(commit, appointmentId);
                      if (payload) saveReeditCardLab(cardId, captured, payload);
                    }}
                    onAddAnother={() => EventService.emit(LI_V2_EDIT, {})}
                    onReject={() => rejectReeditCardLab(cardId, captured)}
                    onClose={() => cancelReeditCardLab(cardId, captured)}
                    themeClass={wasAiLab ? "is-ai" : "is-manual"}
                  />
                );
              })}
              {manualCardIdsLab.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <DigitiseManuallyCard
                    formId={cardId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onAdd={(commit) => {
                      const payload = labCommitToPrimaryCapturedAdd(commit, appointmentId);
                      if (payload) saveManualCardLab(cardId, payload);
                    }}
                    onAddAnother={() => EventService.emit(LI_V2_EDIT, {})}
                    onClose={() => cancelManualCardLab(cardId)}
                    themeClass="is-manual"
                  />
                </div>
              ))}
              {proposals.map((it) => {
                const editing = labCapturedItemsToCardEditing([it]);
                return (
                  <DigitiseManuallyCard
                    key={it.id}
                    formId={it.id}
                    editing={editing}
                    saving={!!saving[it.id]}
                    error={errors[it.id] || ""}
                    onAdd={(commit) => {
                      const payload = labCommitToPrimaryCapturedAdd(commit, appointmentId);
                      if (payload) saveAiCardLab(it, payload);
                    }}
                    onAddAnother={() => EventService.emit(LI_V2_EDIT, {})}
                    onClose={() => discardAi(it)}
                    themeClass="is-ai"
                  />
                );
              })}
              {manualCardIdsLab.length === 0 && proposals.length === 0 && reeditCardsLab.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> Add investigation</strong> on the left to add
                      a manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "allergies" ? (
            <>
              {/* Update cards for existing Allergy v2 rows the reviewer
                  clicked in the middle-column table. */}
              {updateCardsAlg.map(({ cardId, record }) => (
                <AllergyForm
                  key={cardId}
                  formId={cardId}
                  patientId={patientId}
                  editing={record}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveUpdateCardAlg(cardId, record, algBodyToCapturedUpdate(body, { record }))}
                  onClose={() => cancelUpdateCardAlg(cardId)}
                  themeClass="is-update"
                  menuClassName="ait-menu"
                />
              ))}
              {/* Wave B first-cut for allergies: same shape as
                  meds / hosp / lab — manual + AI-proposal + staged-add
                  re-edit. */}
              {reeditCardsAlg.map(({ cardId, captured, algv2Record, initialPayloadOverride }) => {
                const wasAiAlg = captured.source === "ai";
                // ZY-10387 Shift 2: prefer `effective` (null-stripped
                // overlay of LastCommitted + Current) so a reviewer
                // edit held in LastCommitted isn't clobbered by a later
                // AI-only emission.
                const initP = initialPayloadOverride || captured.effective || captured.current;
                // Lazy-resolve algv2Record from algRows if the attached
                // one is null (covers Shift-4 auto-reopens where the
                // record wasn't on hand at card creation time).
                let effectiveRecord = algv2Record;
                if (!effectiveRecord && captured.operation === "update" && captured.dbRecordRef) {
                  effectiveRecord = (algRows || []).find((r) => String(r.allergyId) === String(captured.dbRecordRef)) || null;
                }
                if (captured.operation === "update" && effectiveRecord) {
                  // Overlay the captured's effective payload onto the
                  // backing Allergy v2 record so the staged edits from
                  // the previous Update land in the form (AllergyForm's
                  // isUpdate initializers read exclusively from
                  // `editing`, ignoring `initialPayload`). Without this
                  // overlay a Shift-4 auto-reopen after an AI re-emit
                  // would show the raw DB record and lose the reviewer's
                  // typed Description / adjusted dates / modified
                  // reactions. Mirrors the CC / Hosp reedit overlay.
                  const p = captured.effective || captured.current || {};
                  const toIso = (v) => {
                    if (!v) return undefined;
                    const s = String(v).slice(0, 10);
                    return s || undefined;
                  };
                  const editing = {
                    ...effectiveRecord,
                    category:     p.category || effectiveRecord.category,
                    firstNoted:   toIso(p.since_date) || effectiveRecord.firstNoted,
                    lastReaction: toIso(p.last_reaction_date) || effectiveRecord.lastReaction,
                    reactions:    (Array.isArray(p.reactions) && p.reactions.length > 0)
                                    ? p.reactions
                                    : effectiveRecord.reactions,
                    description:  (p.description !== undefined && p.description !== null)
                                    ? p.description
                                    : effectiveRecord.description,
                  };
                  return (
                    <AllergyForm
                      key={cardId}
                      formId={cardId}
                      patientId={patientId}
                      editing={editing}
                      initialPayload={initP}
                      saving={!!saving[cardId]}
                      error={errors[cardId] || ""}
                      onSave={(body) => saveReeditCardAlg(cardId, captured, algBodyToCapturedUpdate(body, { record: effectiveRecord }))}
                      onReject={() => rejectReeditCardAlg(cardId, captured)}
                      onClose={() => cancelReeditCardAlg(cardId, captured)}
                      themeClass={wasAiAlg ? "is-ai" : "is-update"}
                      lockName
                      /* #6/#8 — DB-update re-open. Keep lockName; add
                         isReopen so the X top-right surfaces. */
                      isReopen
                    />
                  );
                }
                return (
                  <AllergyForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    initialPayload={initP}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardAlg(cardId, captured, algBodyToCapturedAdd(body))}
                    onReject={() => rejectReeditCardAlg(cardId, captured)}
                    onClose={() => cancelReeditCardAlg(cardId, captured)}
                    themeClass={wasAiAlg ? "is-ai" : "is-manual"}
                    /* ZY-10356 Phase 6 Rule 1 — new-entry re-open (#2/#4).
                       Main field (allergen) editable; X top-right from
                       isReopen is the only differentiator. */
                    isReopen
                  />
                );
              })}
              {manualCardIdsAlg.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <AllergyForm
                    formId={cardId}
                    patientId={patientId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onSave={(body) => saveManualCardAlg(cardId, algBodyToCapturedAdd(body))}
                    onClose={() => cancelManualCardAlg(cardId)}
                    themeClass="is-manual"
                  />
                </div>
              ))}
              {proposals.map((it) => {
                // AI-match-to-DB dedup (ZY-10387 safety net for Alg).
                // Match on (name, category) pair — Allergy v2 allows
                // two rows for the same allergen name in different
                // categories (e.g., "shellfish" as Food vs Others), so
                // name-only match would wrongly collapse them. Falls
                // back to name-only when the AI didn't emit a category.
                // Backend Shift-3 auto-promote doesn't apply to Alg
                // (no "ongoing" axis, no code-keyed uniqueKey).
                const propName = ((it.current && (it.current.name || it.current.substance)) || "").trim().toLowerCase();
                const propCat = ((it.current && it.current.category) || "").trim();
                let match = null;
                if (propName && Array.isArray(algRows)) {
                  if (propCat) {
                    match = algRows.find((r) =>
                      (r.name || "").trim().toLowerCase() === propName
                      && (r.category || "").trim() === propCat,
                    );
                  }
                  if (!match) {
                    // Fall back to name-only when category wasn't
                    // emitted. Lower confidence but still better than
                    // skipping dedup entirely.
                    match = algRows.find((r) => (r.name || "").trim().toLowerCase() === propName);
                  }
                }
                if (match) {
                  return (
                    <AllergyForm
                      key={it.id}
                      formId={it.id}
                      patientId={patientId}
                      editing={match}
                      initialPayload={it.current}
                      saving={!!saving[it.id]}
                      error={errors[it.id] || ""}
                      onSave={(body) => saveAiProposalAsUpdate(
                        it, "allergies", match.allergyId,
                        algBodyToCapturedUpdate(body, { record: match }),
                      )}
                      onClose={() => discardAi(it)}
                      themeClass="is-ai"
                      lockName
                      isReopen={!!it.lastCommitted}
                    />
                  );
                }
                return (
                  <AllergyForm
                    key={it.id}
                    formId={it.id}
                    patientId={patientId}
                    initialPayload={it.current}
                    saving={!!saving[it.id]}
                    error={errors[it.id] || ""}
                    onSave={(body) => saveAiCardAlg(it, algBodyToCapturedAdd(body))}
                    onClose={() => discardAi(it)}
                    themeClass="is-ai"
                    /* Revived rejected Alg proposals (ZY-10387 revive
                       branch) carry LastCommitted and read as re-open —
                       show the X close. */
                    isReopen={!!it.lastCommitted}
                  />
                );
              })}
              {manualCardIdsAlg.length === 0 && proposals.length === 0 && reeditCardsAlg.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add allergy</strong> on the left to add a
                      manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "addictions" ? (
            <>
              {/* Wave B first-cut for addictions: fixed 4-slot resource.
                  Update cards (from row-click) sit on top, then AI
                  proposals, then re-edit cards. No "add manually"
                  affordance — every card is an update of an existing
                  row. */}
              {updateCardsAdd.map(({ cardId, addiction }) => (
                <AddictionForm
                  key={cardId}
                  formId={cardId}
                  patientId={patientId}
                  editing={addiction}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveUpdateCardAdd(cardId, addiction, addBodyToCapturedAdd(body))}
                  onClose={() => cancelUpdateCardAdd(cardId)}
                  themeClass="is-update"
                  menuClassName="ait-menu"
                />
              ))}
              {reeditCardsAdd.map(({ cardId, captured: snapshot, addiction }) => {
                // Always resolve the LIVE captured from items on each
                // render — the entry stored in reeditCardsAdd holds a
                // snapshot taken when the reedit card was created, but
                // a mid-review AI re-emit updates items[] in place.
                // Reading the snapshot would freeze the form at the
                // first mention's payload even as later mentions merge
                // on the server. Fall back to the snapshot if the item
                // was deleted / soft-removed between poll ticks.
                // id can be either `_id` or `id` depending on how the
                // transcript endpoint marshaled Mongo's ObjectId; compare
                // both to be safe (coerce to string in case one side is
                // a buffer).
                const snapshotId = String((snapshot && (snapshot.id || snapshot._id)) || "");
                const captured = (items || []).find((x) => {
                  if (!x) return false;
                  const xId = String(x.id || x._id || "");
                  return xId && xId === snapshotId;
                }) || snapshot;
                const wasAiAdd = captured.source === "ai";
                // ZY-10387 Shift 2: prefer `effective` (= LastCommitted ∪
                // Current, nulls stripped) so a reviewer edit held in
                // LastCommitted isn't clobbered by a later AI-only
                // emission. Lastly fall back to the overlaid DB row's
                // own clone built via lastCommitted/current — covers the
                // case where `effective` isn't present on the item (dev
                // against an older server build or a reopen captured
                // before the Shift-2 marshal landed).
                const computed = (() => {
                  const base = {};
                  if (captured.lastCommitted) Object.assign(base, captured.lastCommitted);
                  if (captured.current)       Object.assign(base, captured.current);
                  return base;
                })();
                const payload = captured.effective || (Object.keys(computed).length > 0 ? computed : captured.current) || {};
                const key = payload.key || (addiction && addiction.key);
                const seedEditing = addiction && addiction.key
                  // Full overlaid row — gives AddictionForm a secondary
                  // seed source (reads `addiction.frequency` etc.) so
                  // the form never shows blank when effective merged a
                  // thin AI payload over a committed snapshot.
                  ? addiction
                  : (key ? { key, label: key.charAt(0).toUpperCase() + key.slice(1) } : {});
                // Payload hash in BOTH `key` AND `formId` so a mid-
                // review AI re-emit (same captured, new Current) fully
                // resets the card:
                //   • `key` → React unmount + remount, re-running every
                //     useState initializer against the fresh payload.
                //   • `formId` → fresh useCardDraft bucket, so the
                //     draft-replay path (which would otherwise hydrate
                //     from the previous mention's snapshot) collapses.
                // Without the formId piece, the remount reads the stale
                // draft under the stable cardId and the fresh payload
                // never shows.
                const payloadKey = JSON.stringify(captured.current || {});
                const scopedFormId = `${cardId}::${payloadKey}`;
                return (
                  <AddictionForm
                    key={scopedFormId}
                    formId={scopedFormId}
                    patientId={patientId}
                    editing={seedEditing}
                    initialPayload={payload}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardAdd(cardId, captured, addBodyToCapturedAdd(body))}
                    onReject={() => rejectReeditCardAdd(cardId, captured)}
                    onClose={() => cancelReeditCardAdd(cardId, captured)}
                    themeClass={wasAiAdd ? "is-ai" : "is-manual"}
                    menuClassName="ait-menu"
                    /* #6/#8 — DB-update re-open. Addictions is a fixed
                       4-slot resource so #2/#4 (new-entry re-open)
                       never apply here. */
                    isReopen
                  />
                );
              })}
              {proposals.map((it) => {
                // Seed resolution for an AI proposal card. Precedence
                // walked top → bottom; first truthy field wins per key:
                //   1. captured.effective (= LastCommitted ∪ Current,
                //      Shift-2 server merge, nulls stripped by Shift-1).
                //      Covers the Shift-4 reopen case where the user
                //      committed in-call values and AI re-emitted a
                //      thin delta.
                //   2. local merge of lastCommitted/current (dev
                //      against an older server that didn't marshal
                //      effective).
                //   3. the persisted Addiction v2 row for this slot
                //      (addRows). Covers the FIRST-TIME AI emission on
                //      an addiction with pre-existing DB values — the
                //      slot already has Frequency / Description / Totals
                //      saved from a prior transcript, and we want those
                //      visible under the AI delta.
                const computed = (() => {
                  const base = {};
                  if (it.lastCommitted) Object.assign(base, it.lastCommitted);
                  if (it.current)       Object.assign(base, it.current);
                  return base;
                })();
                const serverOrLocal =
                  it.effective ||
                  (Object.keys(computed).length > 0 ? computed : null) ||
                  it.current ||
                  {};
                const key = serverOrLocal.key || (it.current && it.current.key);
                // DB row for this key (if any) — gives the form a
                // persisted-values fallback at the editing-prop level.
                const dbRow = key
                  ? (addRows || []).find((r) => String(r.key || "").toLowerCase() === String(key).toLowerCase())
                  : null;
                // Also fold DB values UNDER the staged payload so the
                // form's initialPayload (`p`) already has them — this
                // way the form reads p.frequency and gets the DB value
                // even if `editing` is just the stub seed.
                const payload = dbRow
                  ? {
                      // DB fields in captured-payload (snake_case) shape:
                      key:            dbRow.key,
                      status:         dbRow.status,
                      frequency:      dbRow.frequency,
                      description:    dbRow.description,
                      started_years:  (dbRow.startedWhen && dbRow.startedWhen.years)  || 0,
                      started_months: (dbRow.startedWhen && dbRow.startedWhen.months) || 0,
                      started_days:   (dbRow.startedWhen && dbRow.startedWhen.days)   || 0,
                      total_years:    (dbRow.totalInterval && dbRow.totalInterval.years)  || 0,
                      total_months:   (dbRow.totalInterval && dbRow.totalInterval.months) || 0,
                      total_days:     (dbRow.totalInterval && dbRow.totalInterval.days)   || 0,
                      status_update:  dbRow.statusUpdate,
                      // staged payload wins per-key (AI's new emission
                      // + LC overlay from effective):
                      ...serverOrLocal,
                    }
                  : serverOrLocal;
                const seedEditing = dbRow || (key ? { key, label: key.charAt(0).toUpperCase() + key.slice(1) } : {});
                // Payload hash in BOTH `key` AND `formId` so a mid-
                // review AI re-emit (same captured.id, new Current)
                // fully resets the card:
                //   • `key` → React unmount + remount, re-running every
                //     useState initializer against the fresh payload.
                //   • `formId` → fresh useCardDraft bucket. Without this,
                //     the remount reads the draft under the stable
                //     `it.id` and replays the previous mention's state,
                //     so the new "6 months" never surfaces over the old
                //     "3 months" snapshot.
                const payloadKey = JSON.stringify(it.current || {});
                const scopedFormId = `${it.id}::${payloadKey}`;
                return (
                  <AddictionForm
                    key={scopedFormId}
                    formId={scopedFormId}
                    patientId={patientId}
                    editing={seedEditing}
                    initialPayload={payload}
                    saving={!!saving[it.id]}
                    error={errors[it.id] || ""}
                    onSave={(body) => saveAiCardAdd(it, addBodyToCapturedAdd(body))}
                    onClose={() => discardAi(it)}
                    themeClass="is-ai"
                    menuClassName="ait-menu"
                    /* #7 (first-open AI, no lastCommitted) vs #8 (reopen
                       after ZY-10387 revive, carries lastCommitted).
                       Mirrors FH / Alg / PA proposal branches. */
                    isReopen={!!it.lastCommitted}
                  />
                );
              })}
              {updateCardsAdd.length === 0 && proposals.length === 0 && reeditCardsAdd.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Click a
                      row on the left to record a manual update.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "family_history" ? (
            <>
              {/* Update cards for existing FH v2 relatives the reviewer
                  clicked in the middle-column table. */}
              {updateCardsFh.map(({ cardId, record }) => (
                /* DB-update card edits an existing row and locks the
                   relationship — RelativeForm's takenSingles already
                   excludes `editing.relativeId`, so passing the full
                   staged-single list is safe. */
                <RelativeForm
                  key={cardId}
                  formId={cardId}
                  patientId={patientId}
                  editing={record}
                  siblingClaims={fhStagedSingleClaims.map((c) => c.relKey)}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveUpdateCardFh(cardId, record, fhBodyToCapturedUpdate(body, { record }))}
                  onClose={() => cancelUpdateCardFh(cardId)}
                  themeClass="is-update"
                  menuClassName="ait-menu"
                />
              ))}
              {/* Wave B first-cut for family history: same shape as
                  allergies — manual + AI-proposal + staged-add re-edit. */}
              {reeditCardsFh.map(({ cardId, captured, famv2Record, initialPayloadOverride }) => {
                const wasAiFh = captured.source === "ai";
                // ZY-10387 Shift 2: effective overlay fallback.
                const initP = initialPayloadOverride || captured.effective || captured.current;
                // Lazy-resolve famv2Record from fhRows if the attached
                // one is null (covers Shift-4 auto-reopens).
                let effectiveRecord = famv2Record;
                if (!effectiveRecord && captured.operation === "update" && captured.dbRecordRef) {
                  effectiveRecord = (fhRows || []).find((r) => String(r.relativeId) === String(captured.dbRecordRef)) || null;
                }
                // Exclude this card's own staged claim from the sibling
                // list so the reedit doesn't flag itself. Multi-rels
                // never appeared in fhStagedSingleClaims to begin with.
                const ownId = String(captured.id || "");
                const siblingClaimsFh = fhStagedSingleClaims
                  .filter((c) => c.capturedId !== ownId)
                  .map((c) => c.relKey);
                if (captured.operation === "update" && effectiveRecord) {
                  return (
                    <RelativeForm
                      key={cardId}
                      formId={cardId}
                      patientId={patientId}
                      editing={effectiveRecord}
                      initialPayload={initP}
                      siblingClaims={siblingClaimsFh}
                      saving={!!saving[cardId]}
                      error={errors[cardId] || ""}
                      onSave={(body) => saveReeditCardFh(cardId, captured, fhBodyToCapturedUpdate(body, { record: effectiveRecord }))}
                      onReject={() => rejectReeditCardFh(cardId, captured)}
                      onClose={() => cancelReeditCardFh(cardId, captured)}
                      themeClass={wasAiFh ? "is-ai" : "is-update"}
                      lockName
                      menuClassName="ait-menu"
                      /* #6/#8 — DB-update re-open. X top-right. */
                      isReopen
                    />
                  );
                }
                return (
                  <RelativeForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    initialPayload={initP}
                    siblingClaims={siblingClaimsFh}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardFh(cardId, captured, fhBodyToCapturedAdd(body))}
                    onReject={() => rejectReeditCardFh(cardId, captured)}
                    onClose={() => cancelReeditCardFh(cardId, captured)}
                    themeClass={wasAiFh ? "is-ai" : "is-manual"}
                    lockName
                    menuClassName="ait-menu"
                    /* #2/#4 — new-entry re-open. X top-right. */
                    isReopen
                  />
                );
              })}
              {manualCardIdsFh.map((cardId) => (
                /* Manual "+ Add relative" card has no backing captured
                   yet — pass the full staged-singles list so the picker
                   flags any single-only relationship an AI / other
                   staged card has already claimed this call. */
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <RelativeForm
                    formId={cardId}
                    patientId={patientId}
                    siblingClaims={fhStagedSingleClaims.map((c) => c.relKey)}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onSave={(body) => saveManualCardFh(cardId, fhBodyToCapturedAdd(body))}
                    onClose={() => cancelManualCardFh(cardId)}
                    themeClass="is-manual"
                    menuClassName="ait-menu"
                  />
                </div>
              ))}
              {proposals.map((it) => {
                // Dedup by relationship. FH is not name-keyed like PA /
                // Alg — each relative is identified by relationship
                // (Father, Mother, ...) instead. For single-only
                // relationships (Father, Mother, grandparents), a
                // second entry can't coexist, so an AI proposal
                // targeting a taken slot opens as an update on that
                // record. Multi relationships (brother/sister/other)
                // always render as a fresh add — those can legitimately
                // repeat. Normalise the AI's raw string using the same
                // rules RelativeForm's seedRelationshipFromPayload
                // applies (lowercase, "_"/" " → "-").
                const raw = ((it.current && (it.current.relationship || it.current.value)) || "").toString();
                const relKey = raw.trim().toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");
                const match = relKey && isSingleRelationship(relKey)
                  ? (fhRows || []).find((r) => (r.relationship || "").toLowerCase() === relKey)
                  : null;
                const ownId = String(it.id || "");
                const siblingClaimsFh = fhStagedSingleClaims
                  .filter((c) => c.capturedId !== ownId)
                  .map((c) => c.relKey);
                if (match) {
                  return (
                    <RelativeForm
                      key={it.id}
                      formId={it.id}
                      patientId={patientId}
                      editing={match}
                      initialPayload={it.current}
                      siblingClaims={siblingClaimsFh}
                      saving={!!saving[it.id]}
                      error={errors[it.id] || ""}
                      onSave={(body) => saveAiProposalAsUpdate(
                        it, "family_history", match.relativeId,
                        fhBodyToCapturedUpdate(body, { record: match }),
                      )}
                      onClose={() => discardAi(it)}
                      themeClass="is-ai"
                      lockName
                      menuClassName="ait-menu"
                      isReopen={!!it.lastCommitted}
                    />
                  );
                }
                return (
                  <RelativeForm
                    key={it.id}
                    formId={it.id}
                    patientId={patientId}
                    initialPayload={it.current}
                    siblingClaims={siblingClaimsFh}
                    saving={!!saving[it.id]}
                    error={errors[it.id] || ""}
                    onSave={(body) => saveAiCardFh(it, fhBodyToCapturedAdd(body))}
                    onClose={() => discardAi(it)}
                    themeClass="is-ai"
                    menuClassName="ait-menu"
                    /* Revived rejected FH proposals (ZY-10387 revive)
                       carry LastCommitted and read as re-open. */
                    isReopen={!!it.lastCommitted}
                  />
                );
              })}
              {manualCardIdsFh.length === 0 && proposals.length === 0 && reeditCardsFh.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add family member</strong> on the left to
                      add a manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "anthropometric" ? (
            <>
              {/* Wave C first-cut for anthropometric: grouped-parameter
                  model — 5 user-entered parameters (Height, Weight,
                  Blood group, Waist, Hip), plus 2 server-derived
                  (BMI, WHR). The AI proposes per-parameter values;
                  the reviewer confirms via the same ParamEditor the
                  standalone rail uses. Manual entry from the
                  transcript view lands here too (row-name click on the
                  embedded Anthropometric component). */}
              {reeditCardsAnth.map(({ cardId, captured: snapshot }) => {
                // Always resolve the LIVE captured from items — the
                // snapshot in reeditCardsAnth is from card-creation
                // time; a mid-review AI re-emit updates items[] in
                // place. See Addictions reedit for the same pattern.
                const snapshotId = String((snapshot && (snapshot.id || snapshot._id)) || "");
                const captured = (items || []).find((x) => {
                  if (!x) return false;
                  const xId = String(x.id || x._id || "");
                  return xId && xId === snapshotId;
                }) || snapshot;
                const wasAiAnth = captured.source === "ai";
                // Form seeds from `effective` (= LastCommitted ∪ Current,
                // nulls stripped). Fall back for pre-Shift-2 server.
                // IMPORTANT: Current.numericValue (frontend commits) and
                // Current.numeric_value (AI merges) are the same field
                // but different keys — the server's effective merge
                // treats them as independent. Prefer the SNAKE_CASE AI
                // delta when it's present (that's the "latest" write),
                // then fall back to the camelCase LC snapshot. Mirrors
                // the proposals.map normalisation.
                const computed = (() => {
                  const base = {};
                  if (captured.lastCommitted) Object.assign(base, captured.lastCommitted);
                  if (captured.current)       Object.assign(base, captured.current);
                  return base;
                })();
                const payloadRaw = captured.effective
                  || (Object.keys(computed).length > 0 ? computed : captured.current)
                  || {};
                // Promote snake_case AI fields over camelCase LC when
                // the AI's new Current carries them. captured.current is
                // the authoritative "latest write" — its presence of a
                // snake_case key signals an AI merge after the user's
                // commit (whose LC still carries the camelCase keys).
                const currentNow = captured.current || {};
                const payload = (() => {
                  const out = { ...payloadRaw };
                  if (currentNow.numeric_value != null) {
                    out.numericValue = currentNow.numeric_value;
                    out.numeric_value = currentNow.numeric_value;
                  }
                  if (currentNow.value_display != null) {
                    out.valueDisplay = currentNow.value_display;
                    out.value_display = currentNow.value_display;
                  }
                  if (currentNow.test_date != null) {
                    out.testDate = currentNow.test_date;
                    out.test_date = currentNow.test_date;
                  }
                  if (currentNow.unit != null) {
                    out.unit = currentNow.unit;
                  }
                  return out;
                })();
                // Payload hash in BOTH `key` AND `formId` so a mid-
                // review AI re-emit fully resets the card (unmount +
                // remount AND fresh useCardDraft bucket). Mirrors the
                // Addictions reedit pattern.
                const payloadKey = JSON.stringify(captured.current || {});
                const scopedFormId = `${cardId}::${payloadKey}`;
                return (
                  <AnthropometricForm
                    key={scopedFormId}
                    formId={scopedFormId}
                    patientId={patientId}
                    initialPayload={payload}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardAnth(cardId, captured, anthBodyToCapturedAdd(body))}
                    onReject={() => rejectReeditCardAnth(cardId, captured)}
                    onClose={() => cancelReeditCardAnth(cardId, captured)}
                    themeClass={wasAiAnth ? "is-ai" : "is-manual"}
                    /* #6/#8 — DB-update re-open. Anthropometric is a
                       fixed-parameter resource so #2/#4 never apply. */
                    isReopen
                  />
                );
              })}
              {manualCardsAnth.map(({ cardId, parameter }) => (
                /* Manual row-click first-open (#5). AnthropometricForm
                   JIT-fetches the persisted parameter from the
                   Anthropometric v2 service when `initialPayload`
                   carries only `{parameter}`, so no additional seeding
                   is needed here. isReopen omitted (first-open rule). */
                <AnthropometricForm
                  key={cardId}
                  formId={cardId}
                  patientId={patientId}
                  initialPayload={{ parameter }}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveManualCardAnth(cardId, parameter, anthBodyToCapturedAdd(body))}
                  onClose={() => cancelManualCardAnth(cardId)}
                  themeClass="is-manual"
                />
              ))}
              {proposals.map((it) => {
                // AI-proposal seed resolution for anthropometric. Walk:
                //   1. captured.effective (Shift-2 server merge).
                //   2. local merge of lastCommitted/current (fallback
                //      for an old server build).
                //   3. persisted parameter from anthRows — covers the
                //      first-time AI emission on a slot that already
                //      has a prior measurement (unit / date carry over
                //      so a bare value doesn't blank the row).
                // The agent emits captured.current in snake_case
                // (`numeric_value` / `value_display` / `test_date`); the
                // frontend body adapter emits camelCase. DB rows are
                // camelCase too. Normalise the staged payload to
                // camelCase FIRST so the AI's new numeric_value cleanly
                // overwrites the DB's numericValue — otherwise the
                // spread leaves two keys for the same field and the
                // form's camelCase-first reader latches onto the DB
                // value. This was the "card shows 152 instead of AI's
                // 160" bug.
                const computed = (() => {
                  const base = {};
                  if (it.lastCommitted) Object.assign(base, it.lastCommitted);
                  if (it.current)       Object.assign(base, it.current);
                  return base;
                })();
                const serverOrLocalRaw =
                  it.effective ||
                  (Object.keys(computed).length > 0 ? computed : null) ||
                  it.current ||
                  {};
                const serverOrLocal = (() => {
                  const out = { ...serverOrLocalRaw };
                  if (out.numeric_value != null && out.numericValue == null) {
                    out.numericValue = out.numeric_value;
                  }
                  if (out.value_display != null && out.valueDisplay == null) {
                    out.valueDisplay = out.value_display;
                  }
                  if (out.test_date != null && out.testDate == null) {
                    out.testDate = out.test_date;
                  }
                  return out;
                })();
                const param = serverOrLocal.parameter || (it.current && it.current.parameter);
                const dbEntry = param
                  ? (anthRows || []).find((r) => r && r.parameter === param)
                  : null;
                const dbCurrent = dbEntry ? dbEntry.current : null;
                const payload = dbCurrent
                  ? {
                      parameter:    param,
                      numericValue: dbCurrent.numericValue,
                      unit:         dbCurrent.unit || "",
                      valueDisplay: dbCurrent.valueDisplay || "",
                      testDate:     dbCurrent.testDate,
                      // staged payload (now camelCase) wins per-key:
                      ...serverOrLocal,
                    }
                  : serverOrLocal;
                const payloadKey = JSON.stringify(it.current || {});
                const scopedFormId = `${it.id}::${payloadKey}`;
                return (
                  <AnthropometricForm
                    key={scopedFormId}
                    formId={scopedFormId}
                    patientId={patientId}
                    initialPayload={payload}
                    saving={!!saving[it.id]}
                    error={errors[it.id] || ""}
                    onSave={(body) => saveAiCardAnth(it, anthBodyToCapturedAdd(body))}
                    onClose={() => discardAi(it)}
                    themeClass="is-ai"
                    /* #7 (first-open AI, no lastCommitted) vs #8 (reopen
                       after revive, carries lastCommitted). */
                    isReopen={!!it.lastCommitted}
                  />
                );
              })}
              {manualCardsAnth.length === 0 && proposals.length === 0 && reeditCardsAnth.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Click a
                      parameter row on the left to record a manual measurement.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "food_preferences" ? (
            // AI captures for FP land directly in the middle-column
            // FoodPreference form's chip lists (see the FP_V2_AI_INJECT
            // useEffect above), so there are no right-rail cards. Fill
            // the right column with the standard AI-is-listening card
            // whenever nothing from this section has been captured yet,
            // matching the convention of every other section's branch.
            (forSection.length === 0) ? (
              awaitingConsent ? (
                <div className="aic-empty aic-empty-slim aic-empty-warn">
                  <Sparkle className="aic-empty-spark" />
                  <p className="aic-empty-title">Consent required</p>
                  <p className="aic-empty-sub">
                    Click <strong>Consent captured</strong> in the purple banner
                    above once the patient has agreed. AI extraction is blocked
                    until then.
                  </p>
                </div>
              ) : callEnded ? null : (
                <div className="aic-empty aic-empty-slim">
                  <Sparkle className="aic-empty-spark" />
                  <p className="aic-empty-title">AI is listening…</p>
                  <p className="aic-empty-sub">
                    Drafts appear here as the conversation happens.
                    Preferences AI captures land straight into the chip
                    lists on the left — edit them there and hit
                    <strong> Save</strong>.
                  </p>
                </div>
              )
            ) : null
          ) : activeSection === "daily_schedule" ? (
            <>
              {/* Wave C first-cut for daily schedule: AI proposals (one
                  card per captured timeline entry) + review re-edit.
                  Manual add stays in the embedded DailySchedule's own
                  EntryModal (opens through the shared portal above
                  this rail). */}
              {reeditCardsDs.map(({ cardId, captured }) => {
                const wasAiDs = captured.source === "ai";
                return (
                  <ScheduleEntryForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    initialPayload={captured.current}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardDs(cardId, captured, dsBodyToCapturedAdd(body))}
                    onReject={() => rejectReeditCardDs(cardId, captured)}
                    onClose={() => cancelReeditCardDs(cardId, captured)}
                    themeClass={wasAiDs ? "is-ai" : "is-manual"}
                    lockName
                  />
                );
              })}
              {proposals.map((it) => (
                <ScheduleEntryForm
                  key={it.id}
                  formId={it.id}
                  patientId={patientId}
                  initialPayload={it.current}
                  saving={!!saving[it.id]}
                  error={errors[it.id] || ""}
                  onSave={(body) => saveAiCardDs(it, dsBodyToCapturedAdd(body))}
                  onClose={() => discardAi(it)}
                  themeClass="is-ai"
                />
              ))}
              {proposals.length === 0 && reeditCardsDs.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add/Edit schedule</strong> on the left to
                      add a manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "activity" ? (
            <>
              {/* Update cards for existing PA v2 activities the reviewer
                  clicked in the middle-column table (both edit and
                  status-chip clicks route through the same event pair). */}
              {updateCardsAct.map(({ cardId, record }) => (
                <ActivityForm
                  key={cardId}
                  formId={cardId}
                  patientId={patientId}
                  editing={record}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveUpdateCardAct(cardId, record, actBodyToCapturedUpdate(body, { record }))}
                  onClose={() => cancelUpdateCardAct(cardId)}
                  extraActiveKeys={stagedActivityNames}
                  themeClass="is-update"
                  menuClassName="ait-menu"
                />
              ))}
              {/* Wave B first-cut for physical activity: same shape as
                  allergies / FH — manual + AI-proposal + staged-add
                  re-edit. */}
              {reeditCardsAct.map(({ cardId, captured, actv2Record, initialPayloadOverride }) => {
                const wasAiAct = captured.source === "ai";
                // ZY-10387 Shift 2: prefer `effective` (null-stripped
                // overlay of LastCommitted + Current) so a reviewer
                // edit held in LastCommitted isn't clobbered by a later
                // AI-only emission.
                const initP = initialPayloadOverride || captured.effective || captured.current;
                // Lazy-resolve actv2Record from actRows if the attached
                // one is null (Shift-4 auto-reopens where the record
                // wasn't on hand at card creation).
                let effectiveRecord = actv2Record;
                if (!effectiveRecord && captured.operation === "update" && captured.dbRecordRef) {
                  effectiveRecord = (actRows || []).find((r) => String(r.activityId) === String(captured.dbRecordRef)) || null;
                }
                // Staged-update re-edit: seed the form with the live PA
                // v2 record so isEdit=true (skips the duplicate-name
                // check that fires when the row's own name looks like
                // a "new entry" collision) and pre-fills the fields
                // from the record. initialPayload still overlays the
                // reviewer's most recent staged edits on top of that.
                if (captured.operation === "update" && effectiveRecord) {
                  // Overlay the captured's effective payload onto the
                  // backing PA v2 record so staged step-1 edits land in
                  // the form (ActivityForm's initializers prefer the
                  // `editing` field value over `initialPayload` when
                  // both are truthy — same gotcha the Alg reopen fix
                  // worked around). Without the overlay a Shift-4
                  // auto-reopen after an AI re-emit would snap back to
                  // the raw DB record and lose the reviewer's typed
                  // days/minutes/intensity/status/description.
                  const p = captured.effective || captured.current || {};
                  const editing = {
                    ...effectiveRecord,
                    daysPerWeek:       (p.days_per_week != null && p.days_per_week !== "") ? p.days_per_week : effectiveRecord.daysPerWeek,
                    minutesPerSession: (p.minutes_per_session != null && p.minutes_per_session !== "") ? p.minutes_per_session : effectiveRecord.minutesPerSession,
                    intensity:         p.intensity || effectiveRecord.intensity,
                    status:            p.status || effectiveRecord.status,
                    description:       (p.description !== undefined && p.description !== null) ? p.description : effectiveRecord.description,
                  };
                  return (
                    <ActivityForm
                      key={cardId}
                      formId={cardId}
                      patientId={patientId}
                      editing={editing}
                      initialPayload={initP}
                      saving={!!saving[cardId]}
                      error={errors[cardId] || ""}
                      onSave={(body) => saveReeditCardAct(cardId, captured, actBodyToCapturedUpdate(body, { record: effectiveRecord }))}
                      onReject={() => rejectReeditCardAct(cardId, captured)}
                      onClose={() => cancelReeditCardAct(cardId, captured)}
                      extraActiveKeys={stagedActivityNames}
                      themeClass={wasAiAct ? "is-ai" : "is-update"}
                      lockName
                      /* #6/#8 — DB-update re-open. X top-right. */
                      isReopen
                      menuClassName="ait-menu"
                    />
                  );
                }
                return (
                  <ActivityForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    initialPayload={initP}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardAct(cardId, captured, actBodyToCapturedAdd(body))}
                    onReject={() => rejectReeditCardAct(cardId, captured)}
                    onClose={() => cancelReeditCardAct(cardId, captured)}
                    extraActiveKeys={stagedActivityNames}
                    themeClass={wasAiAct ? "is-ai" : "is-manual"}
                    /* #2 — new-entry re-open. Activity picker STAYS
                       editable (no lockName): the staged value shows in
                       the "locked" pill with a × "Change activity"
                       affordance, matching Phase 6 Rule 1 (main field
                       editable on new-entry reopens). Prior code pinned
                       the identity which blocked the reviewer from
                       correcting a mis-captured activity. */
                    isReopen
                    menuClassName="ait-menu"
                  />
                );
              })}
              {manualCardIdsAct.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <ActivityForm
                    formId={cardId}
                    patientId={patientId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onSave={(body) => saveManualCardAct(cardId, actBodyToCapturedAdd(body))}
                    onClose={() => cancelManualCardAct(cardId)}
                    extraActiveKeys={stagedActivityNames}
                    themeClass="is-manual"
                    menuClassName="ait-menu"
                  />
                </div>
              ))}
              {proposals.map((it) => {
                // Dedup against existing PA v2 rows. When an AI proposal's
                // name matches an existing entry (case-insensitive), open
                // the card as an update on that record instead of a
                // duplicate add. Save routes through saveAiProposalAsUpdate
                // which discards the ADD proposal and stages a fresh
                // update captured item.
                const propName = ((it.current && it.current.name) || "").trim().toLowerCase();
                const match = propName
                  ? (actRows || []).find((r) => (r.name || "").trim().toLowerCase() === propName)
                  : null;
                // Key includes a hash of the payload so an agent-merge
                // update (payload changes on a still-open proposal card)
                // remounts the form with the new AI fields instead of
                // freezing at the first-mount initial state. Trade-off:
                // the reviewer's in-progress manual edits reset when
                // the AI updates its capture — matches the user's ask
                // of "keep updating the captured fields in card".
                const payloadKey = JSON.stringify(it.current || {});
                if (match) {
                  return (
                    <ActivityForm
                      key={`${it.id}::${payloadKey}`}
                      formId={it.id}
                      patientId={patientId}
                      editing={match}
                      initialPayload={it.current}
                      saving={!!saving[it.id]}
                      error={errors[it.id] || ""}
                      onSave={(body) => saveAiProposalAsUpdate(
                        it, "activity", match.activityId,
                        actBodyToCapturedUpdate(body, { record: match }),
                      )}
                      onClose={() => discardAi(it)}
                      extraActiveKeys={stagedActivityNames}
                      themeClass="is-ai"
                      lockName
                      menuClassName="ait-menu"
                      /* #7 (first-open AI DB-update, no lastCommitted) vs
                         #8 (reopen after ZY-10387 revive, carries
                         lastCommitted). Mirrors FH/Alg proposal branches. */
                      isReopen={!!it.lastCommitted}
                    />
                  );
                }
                return (
                  <ActivityForm
                    key={`${it.id}::${payloadKey}`}
                    formId={it.id}
                    patientId={patientId}
                    initialPayload={it.current}
                    saving={!!saving[it.id]}
                    error={errors[it.id] || ""}
                    onSave={(body) => saveAiCardAct(it, actBodyToCapturedAdd(body))}
                    onClose={() => discardAi(it)}
                    extraActiveKeys={stagedActivityNames}
                    themeClass="is-ai"
                    menuClassName="ait-menu"
                    /* #3 (first-open AI, no lastCommitted) vs #4 (new-
                       entry re-open after ZY-10387 revive, carries
                       lastCommitted). Mirrors FH/Alg proposal branches. */
                    isReopen={!!it.lastCommitted}
                  />
                );
              })}
              {manualCardIdsAct.length === 0 && proposals.length === 0 && reeditCardsAct.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add activity</strong> on the left to add a
                      manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "patient_details" ? (
            /* Right column — the same rail editor the standalone
               patient page uses. `onManualSave` diverts the form's
               submit into /captured (Wave 2), and `capturedItems`
               lets the form pre-fill from staged values so the
               reviewer sees whichever value will be committed.
               Controlled mode: open / focus / onClose pinned to the
               state we own at the CapturedSections level so a row
               click in the middle column reliably surfaces the form
               here.

               Empty state: when nothing has been captured AND the
               editor isn't open, show the standard AI-is-listening
               card so the right column isn't blank — mirrors every
               other section's convention. */
            (!patientDetailsOpen && forSection.length === 0) ? (
              awaitingConsent ? (
                <div className="aic-empty aic-empty-slim aic-empty-warn">
                  <Sparkle className="aic-empty-spark" />
                  <p className="aic-empty-title">Consent required</p>
                  <p className="aic-empty-sub">
                    Click <strong>Consent captured</strong> in the purple banner
                    above once the patient has agreed. AI extraction is blocked
                    until then.
                  </p>
                </div>
              ) : callEnded ? null : (
                <div className="aic-empty aic-empty-slim">
                  <Sparkle className="aic-empty-spark" />
                  <p className="aic-empty-title">AI is listening…</p>
                  <p className="aic-empty-sub">
                    Drafts appear here as the conversation happens. Click
                    on any field on the left to make a manual change.
                  </p>
                </div>
              )
            ) : (
            <PatientDetailsEditor
              patient={patient}
              onManualSave={saveManualCardPatient}
              /* Reject ghost: soft-delete the staged captured on the
                 server so the table overlay drops it (committed-only
                 filter in PatientDetails.js). Reverts the row to its
                 DB values per user ask. */
              onReject={() => {
                const staged = (forSection || []).find((it) => (it && it.state) !== "reviewed");
                if (!staged || !staged.id) {
                  closePatientDetailsRail();
                  return;
                }
                mutateWithRetry(
                  (v, ok, err) => transcriptService.rejectCaptured(
                    patientId, transcriptId, staged.id,
                    { expectedVersion: v }, ok, err,
                  ),
                  (resp) => {
                    applyMutation(resp);
                    closePatientDetailsRail();
                  },
                  () => {
                    closePatientDetailsRail();
                  },
                );
              }}
              capturedItems={forSection}
              open={patientDetailsOpen}
              focusField={patientDetailsFocus}
              focusAt={patientDetailsFocusAt}
              onClose={closePatientDetailsRail}
              /* Peach + maroon theme when the staged capture came from
                 the AI (confidence > 0); slate/indigo default when
                 the reviewer opened the card manually. Same rule CC
                 uses via `themeClass`. */
              themeClass={(() => {
                const staged = forSection.find((it) => (it && it.state) !== "reviewed");
                if (staged && staged.source === "ai") return "is-ai";
                return "is-manual";
              })()}
              /* ZY-10356 Phase 1 — scope the draft key to this transcript
                 so a draft typed into Patient details on one transcript
                 doesn't leak into another's view. */
              draftKey={transcriptId ? `patient-details:${transcriptId}` : "patient-details"}
            />
            )
          ) : activeSection === "drug_schedule" ? (
            /* DS owns its right-column UX via the keep-mounted
               DrugScheduleEditor below (switching tabs would otherwise
               drop any open AddSheet). The AI-empty-state card here is
               rendered ONLY when the editor has no sheets open AND no
               captured items exist — same semantic as every other
               section's empty branch. Fills the void with "AI is
               listening…" so the reviewer doesn't see a blank column
               before any AI proposal lands. */
            (dsSheetCount === 0 && proposals.length === 0 && reeditCardsDs.length === 0) ? (
              awaitingConsent ? (
                <div className="aic-empty aic-empty-slim aic-empty-warn">
                  <Sparkle className="aic-empty-spark" />
                  <p className="aic-empty-title">Consent required</p>
                  <p className="aic-empty-sub">
                    Click <strong>Consent captured</strong> in the purple banner
                    above once the patient has agreed. AI extraction is blocked
                    until then.
                  </p>
                </div>
              ) : callEnded ? null : (
                <div className="aic-empty aic-empty-slim">
                  <Sparkle className="aic-empty-spark" />
                  <p className="aic-empty-title">AI is listening…</p>
                  <p className="aic-empty-sub">
                    Drafts appear here as the conversation happens. Use
                    <strong> + Add schedule</strong> on the left to add a
                    manual entry.
                  </p>
                </div>
              )
            ) : null
          ) : proposals.length === 0 ? (
            awaitingConsent ? (
              <div className="aic-empty aic-empty-slim aic-empty-warn">
                <Sparkle className="aic-empty-spark" />
                <p className="aic-empty-title">Consent required</p>
                <p className="aic-empty-sub">
                  Click <strong>Consent captured</strong> in the purple banner
                  above once the patient has agreed. AI extraction is blocked
                  until then.
                </p>
              </div>
            ) : callEnded ? null : (
              <div className="aic-empty aic-empty-slim">
                <Sparkle className="aic-empty-spark" />
                <p className="aic-empty-title">AI is listening…</p>
                <p className="aic-empty-sub">
                  Drafts appear here as the conversation happens. Nothing is
                  saved yet — review after the call ends.
                </p>
              </div>
            )
          ) : (
            proposals.map((it) => <ProposalCard key={it.id} item={it} />)
          )}

          {/* ZY-10356 Phase 1 — keep-mounted DrugScheduleEditor so an
              open AddSheet survives tab switches (its useSheetStack
              state is internal and lives with the component instance).
              The modal-mount flag keeps it as the live DS_V2_ADD /
              DS_V2_UPDATE listener (shell editor is gated off). */}
          {hasVisited("drug_schedule") && viewMode !== "review" && (
            <div style={{ display: activeSection === "drug_schedule" ? undefined : "none" }}>
              <DrugScheduleEditor
                patient={patient}
                onManualSave={saveManualCardDrug}
                onManualReject={rejectManualCardDrug}
                onCapturedUpdate={updateManualCardDrug}
                /* Top-right X on a reopened staged card → server
                   revertCaptured. SAFE semantic (resets Current ← LC)
                   vs the footer Reject which is DESTRUCTIVE. Mirrors
                   CC/Dx .zy-card-x. */
                onCapturedRevert={(capturedId) => {
                  if (!capturedId) return;
                  mutateWithRetry(
                    (v, ok, err) => transcriptService.revertCaptured(
                      patientId, transcriptId, capturedId,
                      { expectedVersion: v }, ok, err,
                    ),
                    (resp) => applyMutation(resp),
                    () => {}, // silent — the sheet already closed
                  );
                }}
                stagedDrugNames={stagedDrugSchedNames}
                addThemeClass="is-manual"
                isModalMount
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

CapturedSections.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({
    id:    PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  activeTab:       PropTypes.string.isRequired,
  onSelectTab:     PropTypes.func.isRequired,
  items:           PropTypes.array.isRequired,
  patient:         PropTypes.object,
  /** True during an active call while consentStatus !== "captured".
      Drives an alt empty-state copy on the right column so the user
      knows AI extraction is server-side blocked, not silently broken. */
  awaitingConsent: PropTypes.bool,
  callEnded: PropTypes.bool,
  /** All of the following are only used by the CC card save path. */
  patientId:         PropTypes.number,
  /** Currently-selected appointment on the patient screen. Forwarded
   *  through the LI transcript cards so Lab v2 rows land scoped to
   *  the right appointment. Falsy → the wire omits appointmentId. */
  appointmentId:     PropTypes.number,
  transcriptId:      PropTypes.string,
  transcriptService: PropTypes.object,
  version:           PropTypes.number,
  onVersionSync:     PropTypes.func,
  onItemsSync:       PropTypes.func,
  hideTabs:          PropTypes.bool,
  viewMode:          PropTypes.oneOf(["", "review"]),
  onOpenCountsChange: PropTypes.func,
  /** ZY-10356 Phase 5 — section → review meta. See the destructured
   *  prop comment above for how it's consumed. */
  reviewedSections:  PropTypes.object,
  onReviewedSectionsSync: PropTypes.func,
};

CapturedSections.defaultProps = {
  hideTabs: false,
  viewMode: "",
  patient: null,
  awaitingConsent: false,
  callEnded: false,
  patientId: 0,
  appointmentId: 0,
  transcriptId: "",
  transcriptService: null,
  version: 0,
  onVersionSync: () => {},
  onItemsSync:   () => {},
  onOpenCountsChange: () => {},
  reviewedSections:  null,
  onReviewedSectionsSync: null,
};

export default CapturedSections;
